import { karriereShopInsights } from './insights-karriere-shop';
import { sicherheitInsights } from './insights-sicherheit';

export interface InsightSection {
  heading: string;
  paragraphs: string[];
}

export interface InsightAuthor {
  name: string;
  title: string;
}

export type InsightBlock =
  | { type: 'lead'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered?: boolean; items: string[] }
  | { type: 'callout'; variant?: 'info' | 'tip' | 'warning'; title?: string; text: string }
  | { type: 'image'; base: string; alt: string; caption?: string }
  | { type: 'table'; headers: string[]; rows: string[][]; caption?: string }
  | { type: 'quote'; text: string; cite?: string }
  | { type: 'faq'; items: { question: string; answer: string }[] }
  | { type: 'cta'; title: string; text: string; label: string; to: string };

export interface InsightSeriesPart {
  part: number;
  title: string;
  slug?: string;
}

export interface InsightSeries {
  name: string;
  part: number;
  total: number;
  parts: InsightSeriesPart[];
}

export interface InsightSource {
  label: string;
  url?: string;
  note?: string;
}

export interface Insight {
  slug: string;
  title: string;
  excerpt: string;
  seoTitle?: string;
  seoDescription?: string;
  category: string;
  readingTime: string;
  datePublished: string;
  dateModified?: string;
  heroImage: string;
  heroImageAlt: string;
  cardImagePosition?: string;
  author: InsightAuthor;
  tags: string[];
  sections?: InsightSection[];
  contentBlocks?: InsightBlock[];
  series?: InsightSeries;
  sources?: InsightSource[];
  relatedServices?: string[];
}

export const insights: Insight[] = [
  ...sicherheitInsights,
  ...karriereShopInsights,
  {
    slug: 'ki-kompetenz-eu-ai-act',
    title: 'KI-Kompetenz nach dem EU AI Act: Was Unternehmen jetzt wissen und tun müssen',
    excerpt:
      'Artikel 4 EU AI Act verlangt ein ausreichendes Maß an KI-Kompetenz bei allen, die im Auftrag eines Unternehmens mit KI arbeiten. Was das konkret bedeutet, ob Schulungen Pflicht sind und wie Sie KI-Kompetenz sinnvoll dokumentieren.',
    seoTitle: 'KI-Kompetenz EU AI Act: Pflicht & Schulung',
    seoDescription:
      'Artikel 4 EU AI Act verständlich erklärt: Wer ist betroffen, was bedeutet KI-Kompetenz, brauchen Mitarbeiter eine KI-Schulung – und wie dokumentiert man sie?',
    category: 'KI & Digitalisierung',
    readingTime: '14 Min.',
    datePublished: '2026-08-09',
    heroImage: '/images/ratgeber/ki-kompetenz-eu-ai-act',
    heroImageAlt:
      'Team eines mittelständischen Unternehmens bespricht KI-Nutzung und die Vorgaben des EU AI Act',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead & KI-Experte',
    },
    tags: [
      'EU AI Act',
      'KI-Kompetenz',
      'AI Act Schulung',
      'KI-Schulung Unternehmen',
      'Artikel 4 AI Act',
      'AI Literacy',
      'KMU',
    ],
    contentBlocks: [
      {
        type: 'lead',
        text: 'Mitarbeiter schreiben E-Mails mit ChatGPT, lassen sich Angebotstexte formulieren, recherchieren Lieferanten oder beantworten Kundenanfragen mit KI-Unterstützung. In den meisten Unternehmen passiert das längst – nur weiß die Geschäftsführung oft nicht, welche Werkzeuge im Einsatz sind, welche Daten dort landen und welche Regeln eigentlich gelten. Genau an diesem Punkt setzt Artikel 4 des EU AI Act an.',
      },
      {
        type: 'paragraph',
        text: 'Die gute Nachricht vorweg: Es geht nicht um Bürokratie um ihrer selbst willen und auch nicht um ein Verbot von KI. Es geht darum, dass Menschen, die KI-Systeme im Namen eines Unternehmens nutzen, verstehen, was sie da tun. Dieser Artikel erklärt, was der EU AI Act ist, was Artikel 4 verlangt, wen die Pflicht trifft und wie kleine und mittlere Unternehmen sie mit vertretbarem Aufwand erfüllen können.',
      },
      {
        type: 'callout',
        variant: 'info',
        title: 'Kernpunkte',
        text: 'Artikel 4 EU AI Act verpflichtet Anbieter und Betreiber von KI-Systemen, Maßnahmen für ein ausreichendes Maß an KI-Kompetenz zu ergreifen. Die Pflicht gilt seit dem 2. Februar 2025. Eine bestimmte Schulungsform, eine Mindestdauer oder ein staatliches Zertifikat schreibt die Verordnung nicht vor – wohl aber erwartet sie, dass Unternehmen die Kompetenz ihrer Leute aktiv sicherstellen und das belegen können.',
      },
      { type: 'heading', text: 'Was ist der EU AI Act?' },
      {
        type: 'paragraph',
        text: 'Der EU AI Act ist die [Verordnung (EU) 2024/1689](https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=OJ:L_202401689) – das erste umfassende Regelwerk der Europäischen Union für künstliche Intelligenz. Sie gilt unmittelbar in allen Mitgliedstaaten und muss nicht erst in nationales Recht übertragen werden. Ziel ist ein Binnenmarkt, in dem KI eingesetzt werden kann, ohne Grundrechte, Sicherheit und Gesundheit zu gefährden.',
      },
      {
        type: 'paragraph',
        text: 'Der Ansatz ist risikobasiert: Je größer das potenzielle Risiko eines KI-Systems, desto strenger die Anforderungen. Eine Rechtschreibhilfe wird anders behandelt als ein System, das über Kreditvergaben oder Bewerbungen mitentscheidet.',
      },
      {
        type: 'table',
        headers: ['Risikoklasse', 'Beispiele', 'Konsequenz'],
        rows: [
          [
            'Verboten',
            'Social Scoring durch Behörden, manipulative Systeme, ungezielte Gesichtsbild-Sammlung',
            'Einsatz nicht erlaubt',
          ],
          [
            'Hochrisiko',
            'KI in Personalauswahl, Kreditwürdigkeit, kritischer Infrastruktur, Medizinprodukten',
            'Umfangreiche Pflichten: Risikomanagement, Dokumentation, menschliche Aufsicht',
          ],
          [
            'Begrenztes Risiko',
            'Chatbots, KI-generierte Inhalte, Deepfakes',
            'Transparenzpflichten – Menschen müssen wissen, dass sie es mit KI zu tun haben',
          ],
          [
            'Minimales Risiko',
            'Spam-Filter, Textvorschläge, viele Büro-Anwendungen',
            'Keine besonderen Auflagen – die KI-Kompetenzpflicht gilt trotzdem',
          ],
        ],
        caption:
          'Vereinfachte Übersicht der Risikoklassen. Die Pflicht zur KI-Kompetenz aus Artikel 4 ist nicht auf Hochrisiko-Systeme beschränkt.',
      },
      {
        type: 'paragraph',
        text: 'Wichtig für den Mittelstand: Die meisten Unternehmen bauen keine KI, sie nutzen sie. Für diese Nutzung sind vor allem zwei Dinge relevant – die Transparenzpflichten und eben Artikel 4.',
      },
      { type: 'heading', text: 'Was bedeutet Artikel 4 EU AI Act?' },
      {
        type: 'paragraph',
        text: 'Artikel 4 trägt die Überschrift „KI-Kompetenz". Sinngemäß verlangt er: Anbieter und Betreiber von KI-Systemen ergreifen Maßnahmen, um nach besten Kräften ein ausreichendes Maß an KI-Kompetenz bei ihrem Personal und anderen Personen sicherzustellen, die in ihrem Auftrag mit dem Betrieb und der Nutzung von KI-Systemen befasst sind.',
      },
      {
        type: 'paragraph',
        text: 'Der Wortlaut nennt ausdrücklich drei Faktoren, an denen sich das „ausreichende Maß" bemisst: die technischen Kenntnisse, die Erfahrung, Ausbildung und Schulung der betroffenen Personen, der Kontext, in dem die KI-Systeme eingesetzt werden, und die Personengruppen, bei denen die Systeme eingesetzt werden. Es gibt also bewusst keinen einheitlichen Standard – ein Handwerksbetrieb, der KI für Angebotstexte nutzt, braucht ein anderes Kompetenzniveau als eine Personalabteilung, die KI im Bewerbungsprozess einsetzt.',
      },
      {
        type: 'quote',
        text: 'Nicht: „Jeder braucht dieselbe Schulung." Sondern: „Jeder braucht so viel Verständnis, wie seine konkrete KI-Nutzung erfordert."',
      },
      {
        type: 'paragraph',
        text: 'Ergänzend beschreibt die Verordnung KI-Kompetenz als die Fähigkeiten, Kenntnisse und das Verständnis, die es ermöglichen, KI-Systeme sachkundig einzusetzen, sich der Chancen und Risiken bewusst zu sein und mögliche Schäden einzuschätzen. Es geht also nicht um Programmierkenntnisse, sondern um urteilsfähige Nutzung.',
      },
      { type: 'heading', text: 'Seit wann gilt die Pflicht zur KI-Kompetenz?' },
      {
        type: 'paragraph',
        text: 'Der EU AI Act ist am 1. August 2024 in Kraft getreten und wird gestaffelt anwendbar. Die Vorschriften zu verbotenen Praktiken und die Pflicht zur KI-Kompetenz aus Artikel 4 gelten seit dem **2. Februar 2025**. Weitere Teile – etwa Regelungen zu KI-Modellen mit allgemeinem Verwendungszweck und zu Hochrisiko-Systemen – greifen zeitlich versetzt danach.',
      },
      {
        type: 'paragraph',
        text: 'Die Pflicht ist damit heute geltendes Recht. Gleichzeitig ist die Umsetzung in Bewegung: Auf europäischer Ebene wird über Vereinfachungen und zeitliche Anpassungen einzelner Teile des Regelwerks diskutiert, und in Deutschland ist die nationale Aufsichtsstruktur noch im Aufbau. Die [Bundesnetzagentur](https://www.bundesnetzagentur.de/DE/Fachthemen/Digitales/KI/start.html) hat dazu eine KI-Servicestelle eingerichtet, die Unternehmen informiert und begleitet. Für die Praxis heißt das: Verlassen Sie sich nicht auf eine Schonfrist, aber verfallen Sie auch nicht in Aktionismus.',
      },
      {
        type: 'callout',
        variant: 'warning',
        title: 'Kein Bußgeld-Automatismus – aber Verantwortung',
        text: 'Für Artikel 4 ist kein eigener, unmittelbar auf die KI-Kompetenz bezogener Bußgeldtatbestand vorgesehen. Wer aber im Schadensfall nicht belegen kann, dass er sein Team überhaupt in die Lage versetzt hat, KI verantwortlich zu nutzen, steht in Haftungs-, Datenschutz- und Arbeitsrechtsfragen deutlich schlechter da.',
      },
      { type: 'heading', text: 'Wer muss KI-Kompetenz sicherstellen?' },
      {
        type: 'paragraph',
        text: 'Die Verordnung unterscheidet vor allem zwischen Anbietern und Betreibern. **Anbieter** entwickeln ein KI-System oder lassen es entwickeln und bringen es unter eigenem Namen auf den Markt. **Betreiber** setzen ein KI-System unter eigener Verantwortung im beruflichen Kontext ein. Der weitaus größte Teil kleiner und mittlerer Unternehmen fällt in die zweite Gruppe – oft ohne es zu wissen.',
      },
      {
        type: 'table',
        headers: ['Typische Situation im KMU', 'Rolle', 'Was das bedeutet'],
        rows: [
          [
            'Team nutzt ChatGPT, Claude, Gemini oder Copilot für Texte und Recherche',
            'Betreiber',
            'Artikel 4 greift: Nutzer brauchen ein ausreichendes Verständnis der Grenzen und Risiken',
          ],
          [
            'KI-Chatbot auf der eigenen Website beantwortet Kundenfragen',
            'Betreiber (zusätzlich Transparenzpflicht)',
            'Besucher müssen erkennen können, dass sie mit einer KI sprechen',
          ],
          [
            'KI-gestützte Vorauswahl von Bewerbungen',
            'Betreiber im sensiblen Bereich',
            'Deutlich höheres Kompetenz- und Sorgfaltsniveau, ggf. Hochrisiko-Einstufung',
          ],
          [
            'Eigenes KI-Feature wird in ein Produkt eingebaut und vermarktet',
            'Anbieter',
            'Zusätzliche Anbieterpflichten je nach Risikoklasse',
          ],
        ],
      },
      {
        type: 'paragraph',
        text: 'Die Pflicht umfasst ausdrücklich nicht nur Festangestellte, sondern alle Personen, die in Ihrem Auftrag mit den Systemen arbeiten – also auch Werkstudenten, Freelancer, Aushilfen oder ehrenamtlich Tätige in Vereinen. Eine Untergrenze nach Unternehmensgröße gibt es nicht: Auch ein Betrieb mit fünf Mitarbeitenden ist Betreiber, wenn dort KI genutzt wird.',
      },
      { type: 'heading', text: 'Was bedeutet „KI-Kompetenz" konkret?' },
      {
        type: 'paragraph',
        text: 'KI-Kompetenz ist kein Informatikstudium. Sie beschreibt ein praktisches Urteilsvermögen, das sich in etwa auf diese Bausteine herunterbrechen lässt:',
      },
      {
        type: 'list',
        items: [
          '**Grundlegendes Verständnis von KI:** Was ist ein Sprachmodell, woher kommen die Antworten, warum ist das keine Datenbankabfrage?',
          '**Chancen und Grenzen:** Wofür eignet sich KI im eigenen Arbeitsalltag wirklich – und wofür ausdrücklich nicht?',
          '**Risiken:** Falsche Ergebnisse, erfundene Quellen, Verzerrungen in den Trainingsdaten, blinde Übernahme von Empfehlungen.',
          '**Datenschutz:** Welche personenbezogenen Daten dürfen überhaupt in ein KI-Tool eingegeben werden – und welche nie?',
          '**Vertraulichkeit:** Kundendaten, Kalkulationen, Verträge und Geschäftsgeheimnisse gehören nicht ungeprüft in ein beliebiges Chatfenster.',
          '**Menschliche Kontrolle:** Wer prüft KI-Ergebnisse, bevor sie nach außen gehen oder eine Entscheidung beeinflussen?',
          '**Sichere Nutzung:** Freigegebene Tools, Zugänge, Accounts, Umgang mit Trainingsdaten-Einstellungen.',
          '**Rollen und Verantwortlichkeiten:** Wer entscheidet über neue KI-Tools, wer ist Ansprechpartner bei Zweifeln, wer dokumentiert?',
        ],
      },
      {
        type: 'paragraph',
        text: 'Diese Bausteine sind kein starrer Katalog, sondern ein Raster. Für ein Team, das KI ausschließlich für interne Textentwürfe nutzt, reicht ein solides Grundniveau. Wer KI in Kundenkommunikation, Personalentscheidungen oder Preisgestaltung einsetzt, braucht mehr.',
      },
      {
        type: 'image',
        base: '/images/ratgeber/ki-kompetenz-eu-ai-act',
        alt: 'Besprechung im Unternehmen zu KI-Nutzung, Datenschutz und den Vorgaben des EU AI Act',
        caption:
          'KI-Kompetenz entsteht dort, wo Teams gemeinsam klären, welche Werkzeuge sie wofür einsetzen dürfen.',
      },
      { type: 'heading', text: 'Brauchen Mitarbeiter eine KI-Schulung?' },
      {
        type: 'paragraph',
        text: 'Der EU AI Act schreibt keine bestimmte Schulungsform vor. Er verlangt „Maßnahmen" – und lässt Unternehmen bewusst Spielraum, wie sie diese gestalten. Eine Schulung ist dabei die naheliegendste und zugleich am einfachsten dokumentierbare Maßnahme, aber nicht die einzige.',
      },
      {
        type: 'list',
        items: [
          'Interne oder externe Schulungen und Workshops',
          'Verbindliche interne KI-Richtlinie mit klaren Do\'s und Don\'ts',
          'Einweisungen bei der Einführung eines neuen KI-Tools',
          'Onboarding-Bausteine für neue Mitarbeitende',
          'Regelmäßige Auffrischungen, wenn sich Tools oder Einsatzzwecke ändern',
        ],
      },
      {
        type: 'paragraph',
        text: 'Entscheidend ist nicht das Format, sondern das Ergebnis: Die Menschen, die KI nutzen, müssen die Systeme sachkundig einsetzen können. In der Praxis hat sich eine Kombination bewährt – eine gemeinsame Basisschulung für alle, eine schriftliche Richtlinie als Nachschlagewerk und vertiefende Einheiten für Bereiche mit sensibleren Anwendungsfällen.',
      },
      { type: 'heading', text: 'Brauchen Unternehmen ein KI-Zertifikat?' },
      {
        type: 'paragraph',
        text: 'Nein. Der EU AI Act schreibt kein allgemeines staatliches KI-Kompetenz-Zertifikat vor. Es gibt keine gesetzlich vorgeschriebene Prüfung, die Mitarbeitende ablegen müssten, und keine Behörde, die Unternehmen ein „AI-Act-Siegel" für die Kompetenz ihres Personals ausstellt. Wer Ihnen etwas anderes verkaufen möchte, sollte das sehr gut begründen können.',
      },
      {
        type: 'table',
        headers: ['', 'Teilnahmebestätigung', 'Compliance-Zertifizierung'],
        rows: [
          [
            'Was sie aussagt',
            'Diese Person hat an dieser Maßnahme mit diesen Inhalten teilgenommen',
            'Ein System oder eine Organisation erfüllt einen definierten Standard',
          ],
          [
            'Wer sie ausstellt',
            'Der Anbieter der Schulung',
            'Akkreditierte Stellen, je nach Standard und Anwendungsbereich',
          ],
          [
            'Rolle beim AI Act',
            'Praktischer Nachweis für ergriffene Maßnahmen nach Artikel 4',
            'Für Artikel 4 nicht vorgeschrieben; relevant eher bei Hochrisiko-Systemen und Managementsystemen',
          ],
        ],
        caption:
          'Eine Teilnahmebestätigung dokumentiert eine Maßnahme – sie ersetzt keine rechtliche Bewertung und macht ein Unternehmen nicht automatisch AI-Act-konform.',
      },
      {
        type: 'callout',
        variant: 'warning',
        title: 'Vorsicht bei „AI-Act-zertifiziert"',
        text: 'Kein Schulungsanbieter kann Ihnen zusichern, dass Ihr Unternehmen durch die Teilnahme an einer Schulung insgesamt AI-Act-konform ist. Konformität hängt davon ab, welche Systeme Sie einsetzen, in welcher Rolle und mit welchen Prozessen. Eine Schulung ist ein wichtiger Baustein – mehr aber auch nicht.',
      },
      { type: 'heading', text: 'Welche Inhalte sollte eine gute KI-Schulung enthalten?' },
      {
        type: 'paragraph',
        text: 'Eine sinnvolle KI-Schulung für Unternehmen verbindet Verständnis, Praxis und Regeln. Diese Bausteine haben sich als tragfähig erwiesen:',
      },
      {
        type: 'list',
        items: [
          '**KI-Grundlagen:** Wie funktionieren Sprachmodelle im Prinzip, warum antworten sie unterschiedlich, was ist ein Kontextfenster?',
          '**Generative KI:** Unterschiede zwischen Text-, Bild-, Audio- und Codemodellen und was das für den Arbeitsalltag bedeutet.',
          '**Praktische Nutzung:** Konkrete Anwendungsfälle aus dem eigenen Betrieb statt abstrakter Demos.',
          '**Prompting:** Wie man Aufgaben so formuliert, dass brauchbare Ergebnisse entstehen – inklusive Kontext, Rolle, Format und Beispielen.',
          '**Halluzinationen:** Warum Modelle Dinge erfinden, wie man das erkennt und welche Prüfschritte zwingend nötig sind.',
          '**Datenschutz:** Umgang mit personenbezogenen Daten, Auftragsverarbeitung, Serverstandort, Trainingsdaten-Einstellungen.',
          '**Urheberrecht:** Rechte an Eingaben und Ergebnissen, Umgang mit fremden Texten und Bildern, Kennzeichnung.',
          '**Geschäftsgeheimnisse:** Welche Informationen das Haus nicht verlassen dürfen und wie man dafür Alternativen schafft.',
          '**EU AI Act:** Rollen, Risikoklassen, Transparenzpflichten und die Kompetenzpflicht aus Artikel 4 – praxisnah eingeordnet.',
          '**Interne KI-Richtlinien:** Wie man freigegebene Tools, Verantwortlichkeiten und Prüfschritte verbindlich festhält.',
          '**Menschliche Kontrolle:** Vier-Augen-Prinzip, Freigabewege und der Grundsatz, dass die Verantwortung beim Menschen bleibt.',
          '**KI-Agenten:** Was Systeme leisten, die eigenständig Schritte ausführen – und warum hier besonders klare Grenzen nötig sind.',
        ],
      },
      { type: 'heading', text: 'Beispiele: KI im Arbeitsalltag' },
      {
        type: 'paragraph',
        text: 'Abstrakte Regeln bleiben abstrakt, bis man sie an echten Situationen durchspielt. Typische Beispiele aus kleinen und mittleren Unternehmen:',
      },
      {
        type: 'list',
        items: [
          '**Handwerksbetrieb:** Angebotstexte und Baustellenberichte werden mit KI vorformuliert. Risiko: Preise, Leistungsbeschreibungen und Fristen werden ungeprüft übernommen.',
          '**Hotel und Gastronomie:** Antworten auf Gästebewertungen und Buchungsanfragen entstehen mit KI-Unterstützung. Risiko: Gästedaten landen im Chatfenster, Tonalität passt nicht zum Haus.',
          '**Immobilienunternehmen:** Objektbeschreibungen und Exposé-Texte werden generiert. Risiko: erfundene Ausstattungsmerkmale und fehlende Pflichtangaben.',
          '**Steuerberatung:** Entwürfe für Mandantenschreiben und Zusammenfassungen langer Dokumente. Risiko: Mandantengeheimnis und Berufsrecht – hier braucht es besonders klare Regeln zu Tools und Daten.',
          '**Dienstleister und Agenturen:** Recherche, Konzeptentwürfe, Social-Media-Texte. Risiko: ungeprüfte Quellen und urheberrechtlich problematische Inhalte.',
          '**Verein:** Mitgliederrundschreiben, Förderanträge, Protokolle. Risiko: Ehrenamtliche nutzen private Accounts ohne jede Absprache.',
        ],
      },
      {
        type: 'callout',
        variant: 'tip',
        title: 'Praxistipp',
        text: 'Sammeln Sie vor einer Schulung drei bis fünf reale Aufgaben aus Ihrem Betrieb. An echten Fällen lernt ein Team mehr als an jedem Beispiel aus dem Lehrbuch – und Sie sehen sofort, wo Ihre Regeln noch Lücken haben.',
      },
      { type: 'heading', text: 'Welche Fehler sollten Unternehmen vermeiden?' },
      {
        type: 'list',
        items: [
          '**Wildwuchs bei Tools:** Jeder nutzt, was er kennt – vom privaten Account bis zur unbekannten Browser-Erweiterung.',
          '**Vertrauliche Daten ungeprüft eingeben:** Kundendaten, Kalkulationen, Verträge oder Bewerbungsunterlagen in ein beliebiges KI-Tool kopieren.',
          '**KI-Texte ungeprüft veröffentlichen:** Fachlich falsche Aussagen, erfundene Quellen oder rechtlich heikle Formulierungen gehen direkt nach außen.',
          '**Keinen Überblick haben:** Niemand weiß, welche KI-Systeme im Unternehmen überhaupt im Einsatz sind.',
          '**Keine internen Regeln:** Ohne schriftliche Leitplanken bleibt jede Einweisung folgenlos.',
          '**Maßnahmen nicht dokumentieren:** Es wird geschult, aber nichts festgehalten – im Ernstfall ist die Maßnahme dann nicht belegbar.',
          '**Einmal und nie wieder:** Tools und Funktionen ändern sich schnell; eine Schulung von vor zwei Jahren bildet den Stand nicht mehr ab.',
        ],
      },
      { type: 'heading', text: 'Wie können Unternehmen KI-Kompetenz dokumentieren?' },
      {
        type: 'paragraph',
        text: 'Dokumentation ist der Teil, der am häufigsten vergessen wird – und der im Zweifel den Unterschied macht. Ein einfaches Dokument pro Maßnahme genügt in der Regel. Halten Sie fest:',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          'Datum und Ort der Maßnahme',
          'Teilnehmende Personen mit Funktion oder Abteilung',
          'Inhalte der Schulung oder Einweisung',
          'Dauer der Maßnahme',
          'Eingesetzte bzw. behandelte KI-Systeme und Tools',
          'Behandelte Risiken – Datenschutz, Vertraulichkeit, Fehlerquellen, Urheberrecht',
          'Referent oder verantwortliche Person',
          'Teilnahmebestätigung oder Unterschriftenliste',
          'Verweis auf die interne KI-Richtlinie in der jeweils gültigen Fassung',
          'Termin für die nächste Auffrischung',
        ],
      },
      {
        type: 'paragraph',
        text: 'Ergänzend lohnt sich eine schlichte Liste der eingesetzten KI-Systeme: Tool, Zweck, verantwortliche Person, Art der verarbeiteten Daten, Freigabestatus. Diese Übersicht hilft nicht nur beim AI Act, sondern auch bei Datenschutz-Fragen und beim IT-Überblick.',
      },
      { type: 'heading', text: 'Wie läuft unsere KI-Kompetenz-Schulung ab?' },
      {
        type: 'paragraph',
        text: 'Wir bieten eine kompakte Schulung von rund drei Stunden an – online oder auf Wunsch bei Ihnen vor Ort. Sie ist so aufgebaut, dass Teilnehmende ohne Vorkenntnisse mitkommen und Fortgeschrittene trotzdem etwas mitnehmen.',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          '**KI verstehen:** Wie Sprachmodelle arbeiten, was sie können und wo ihre Grenzen liegen.',
          '**KI praktisch einsetzen:** Prompting, typische Anwendungsfälle, gemeinsames Ausprobieren an Aufgaben aus Ihrem Betrieb.',
          '**Risiken & Datenschutz:** Halluzinationen, personenbezogene Daten, Geschäftsgeheimnisse, Urheberrecht, Prüfschritte.',
          '**EU AI Act:** Rollen, Risikoklassen, Transparenzpflichten und Artikel 4 – verständlich und ohne Paragrafenreiterei.',
          '**KI im eigenen Unternehmen:** Welche Werkzeuge passen zu Ihnen, welche Regeln brauchen Sie, wie sieht der nächste Schritt aus?',
        ],
      },
      {
        type: 'paragraph',
        text: 'Optional ergänzen wir eine individuelle Use-Case-Analyse: Wir schauen uns vorab Ihre Prozesse an und bereiten die Schulung auf Ihre konkreten Anwendungsfälle zu. Alle Teilnehmenden erhalten eine Teilnahmebestätigung mit Datum, Dauer und Inhalten sowie eine Vorlage für eine interne KI-Richtlinie, die Sie an Ihr Haus anpassen können.',
      },
      { type: 'heading', text: 'Warum „Enno und Jens"?' },
      {
        type: 'paragraph',
        text: 'Wir sind eine Agentur aus Wernigerode im Harz und arbeiten in zwei Welten zugleich: Wir bauen Webseiten und digitale Prozesse – und wir setzen KI seit Jahren selbst produktiv ein, von generativen Modellen über Automatisierungen bis zu lokal betriebenen Open-Source-Modellen.',
      },
      {
        type: 'list',
        items: [
          'Praktische Erfahrung aus echten Projekten statt reiner Theorie',
          'Keine juristische Pflichtveranstaltung, sondern eine Schulung, die im Alltag ankommt',
          'Verständliche Sprache – auch für Teams ohne IT-Hintergrund',
          'Konkrete Beispiele aus Ihrer Branche und Ihrem Unternehmen',
          'Herstellerunabhängig: Claude, Gemini, Microsoft Copilot und lokale Open-Source-Modelle',
          'Regionale Betreuung im Harz, auf Wunsch bei Ihnen vor Ort – oder deutschlandweit online',
        ],
      },
      {
        type: 'callout',
        variant: 'info',
        title: 'Was wir nicht tun',
        text: 'Wir bieten keine Rechtsberatung an und bewerten nicht rechtsverbindlich, ob Ihr Unternehmen den EU AI Act erfüllt. Wir vermitteln Kompetenz, ordnen die Anforderungen praxisnah ein und helfen bei Regeln und Dokumentation. Für die rechtliche Bewertung ziehen Sie bitte eine qualifizierte Rechtsberatung hinzu – gern begleiten wir Sie dabei fachlich.',
      },
      { type: 'heading', text: 'Checkliste: Ist unser Unternehmen bei KI gut aufgestellt?' },
      {
        type: 'list',
        ordered: true,
        items: [
          'Wissen wir, welche KI-Systeme in unserem Unternehmen tatsächlich genutzt werden?',
          'Gibt es freigegebene Tools – und ist klar, welche nicht genutzt werden dürfen?',
          'Ist geregelt, welche Daten niemals in ein KI-Tool eingegeben werden dürfen?',
          'Haben alle, die KI nutzen, ein Grundverständnis von Chancen, Grenzen und Risiken?',
          'Gibt es eine schriftliche interne KI-Richtlinie, die alle kennen?',
          'Ist festgelegt, wer KI-Ergebnisse prüft, bevor sie nach außen gehen?',
          'Wissen unsere Mitarbeitenden, was Halluzinationen sind und wie sie damit umgehen?',
          'Haben wir Schulungen und Einweisungen nachvollziehbar dokumentiert?',
          'Gibt es eine feste Ansprechperson für Fragen rund um KI?',
          'Ist ein Termin für die nächste Auffrischung gesetzt?',
        ],
      },
      {
        type: 'paragraph',
        text: 'Wenn Sie mehr als drei dieser Fragen mit „nein" oder „weiß nicht" beantworten, besteht Handlungsbedarf – meist mit überschaubarem Aufwand.',
      },
      { type: 'heading', text: 'Fazit' },
      {
        type: 'paragraph',
        text: 'KI-Kompetenz ist mehr als eine gesetzliche Pflicht. Sie ist die Voraussetzung dafür, dass KI im Unternehmen sicher und produktiv eingesetzt wird. Artikel 4 EU AI Act verlangt keine Zertifikate und keine bestimmte Schulungsdauer – er verlangt, dass Sie Ihre Leute befähigen und das nachvollziehbar tun.',
      },
      {
        type: 'paragraph',
        text: 'Unternehmen, die klare Regeln haben, geeignete Werkzeuge freigeben und ihr Team schulen, erfüllen nicht nur eine Anforderung. Sie holen aus KI deutlich mehr heraus als Betriebe, in denen jeder auf eigene Faust experimentiert.',
      },
      { type: 'heading', text: 'Häufige Fragen zur KI-Kompetenz nach dem EU AI Act' },
      {
        type: 'faq',
        items: [
          {
            question: 'Gilt die Pflicht zur KI-Kompetenz auch für kleine Unternehmen?',
            answer:
              'Ja. Artikel 4 EU AI Act kennt keine Untergrenze nach Unternehmensgröße. Sobald ein Betrieb KI-Systeme im beruflichen Kontext einsetzt, gilt er als Betreiber. Der Umfang der Maßnahmen darf aber zum Risiko und zur Nutzung passen – bei einfachen Anwendungsfällen genügt entsprechend weniger.',
          },
          {
            question: 'Brauchen wir ein Zertifikat, um AI-Act-konform zu sein?',
            answer:
              'Nein. Der EU AI Act schreibt kein allgemeines staatliches KI-Kompetenz-Zertifikat und keine Pflichtprüfung für Mitarbeitende vor. Eine Teilnahmebestätigung dokumentiert lediglich, dass eine Schulungsmaßnahme stattgefunden hat – sie ist ein Nachweis für ergriffene Maßnahmen, aber kein Konformitätssiegel.',
          },
          {
            question: 'Wie lange muss eine KI-Schulung dauern?',
            answer:
              'Eine gesetzlich vorgeschriebene Mindestdauer gibt es nicht. Maßgeblich ist, ob die Teilnehmenden danach in der Lage sind, die eingesetzten KI-Systeme sachkundig und risikobewusst zu nutzen. In der Praxis hat sich für den Einstieg ein Format von etwa drei Stunden bewährt, ergänzt um Auffrischungen.',
          },
          {
            question: 'Reicht eine schriftliche KI-Richtlinie aus?',
            answer:
              'Eine Richtlinie ist ein wichtiger Baustein, ersetzt aber selten das Verständnis. Wer nur ein Dokument verteilt, ohne es zu erklären, riskiert, dass Regeln nicht verstanden und nicht befolgt werden. Sinnvoll ist die Kombination aus Schulung, Richtlinie und dokumentierter Einweisung bei neuen Tools.',
          },
          {
            question: 'Gilt Artikel 4 auch für kostenlose KI-Tools wie ChatGPT?',
            answer:
              'Ja. Entscheidend ist nicht der Preis des Tools, sondern dass es im Auftrag und im Kontext des Unternehmens eingesetzt wird. Gerade kostenlose Zugänge über private Accounts sind aus Datenschutz- und Vertraulichkeitssicht häufig die riskanteste Variante.',
          },
          {
            question: 'Wer ist im Unternehmen für KI-Kompetenz verantwortlich?',
            answer:
              'Die Verantwortung liegt beim Unternehmen als Anbieter oder Betreiber, also in der Regel bei der Geschäftsführung. Sie kann Aufgaben delegieren – etwa an IT, Datenschutzbeauftragte oder eine benannte KI-Ansprechperson – bleibt aber für die Umsetzung verantwortlich.',
          },
          {
            question: 'Wie oft sollte eine KI-Schulung wiederholt werden?',
            answer:
              'Eine feste gesetzliche Frist gibt es nicht. Da sich Werkzeuge und Funktionen schnell verändern, ist eine jährliche Auffrischung ein sinnvoller Richtwert – zusätzlich immer dann, wenn neue KI-Systeme eingeführt oder bestehende erheblich verändert werden.',
          },
          {
            question: 'Was passiert, wenn wir nichts tun?',
            answer:
              'Für Artikel 4 selbst ist kein eigener Bußgeldtatbestand vorgesehen. Praktisch relevanter sind die Folgeprobleme: Datenschutzverstöße, offengelegte Geschäftsgeheimnisse, fehlerhafte Inhalte gegenüber Kunden und eine schlechtere Position, wenn Sie im Streitfall nicht belegen können, dass Sie Ihr Team befähigt haben.',
          },
        ],
      },
      {
        type: 'cta',
        title: 'KI-Kompetenz im Unternehmen aufbauen',
        text: 'Sie möchten wissen, wie eine KI-Schulung für Ihr Unternehmen aussehen kann? Unsere Schulung „KI-Kompetenz im Unternehmen“ vermittelt in 2 bis 5 Stunden die Praxis generativer KI und die Grundlagen des EU AI Act – inklusive Teilnahmebestätigung und Dokumentation der Schulungsmaßnahme.',
        label: 'Zur KI-Kompetenz-Schulung',
        to: '/leistungen/ki-kompetenz-schulung',
      },

      {
        type: 'paragraph',
        text: 'Lieber erst einmal unverbindlich sprechen? Auch das geht: [Unverbindlich sprechen](/kontakt) – wir klären in 20 Minuten, wo Ihr Unternehmen steht und was sinnvoll wäre. Mehr zu unserem Angebot finden Sie auf der Seite [KI-Beratung](/leistungen/ki-beratung), einen Blick auf die nächste Entwicklungsstufe gibt der Artikel [KI-Agentensysteme](/ratgeber/ki-agentensysteme-automatisierung-lokale-modelle).',
      },
      { type: 'heading', text: 'Rechtlicher Hinweis' },
      {
        type: 'callout',
        variant: 'info',
        text: 'Dieser Artikel dient der allgemeinen Information und stellt keine Rechtsberatung dar. Für die konkrete rechtliche Bewertung des Einsatzes von KI im eigenen Unternehmen sollte bei Bedarf eine entsprechend qualifizierte Rechtsberatung hinzugezogen werden.',
      },
    ],
    sources: [
      {
        label: 'Verordnung (EU) 2024/1689 (EU AI Act), insbesondere Artikel 3 und Artikel 4',
        url: 'https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=OJ:L_202401689',
        note: 'EUR-Lex, amtlicher Text',
      },
      {
        label: 'Europäische Kommission – AI Act und Hinweise zur KI-Kompetenz (AI literacy)',
        url: 'https://digital-strategy.ec.europa.eu/de/policies/regulatory-framework-ai',
      },
      {
        label: 'Bundesnetzagentur – KI-Servicestelle und Informationen zum AI Act',
        url: 'https://www.bundesnetzagentur.de/DE/Fachthemen/Digitales/KI/start.html',
      },
      {
        label: 'Bundesministerium für Digitales und Staatsmodernisierung – Informationen zu KI-Regulierung',
        url: 'https://www.bmds.bund.de/',
      },
    ],
    relatedServices: ['ki-kompetenz-schulung', 'ki-beratung', 'digitalisierung-beratung'],
  },

  {
    slug: 'geo-warum-unsichtbar-bei-ki',
    title: 'Warum Ihr Unternehmen bei ChatGPT unsichtbar ist – und was GEO dagegen tut',
    excerpt:
      'Fast 70 % der Google-Suchen enden 2026 ohne Klick. Warum der Mittelstand in KI-Antworten fehlt – und was Generative Engine Optimization (GEO) dagegen tut.',
    seoTitle: 'GEO für KMU: Warum Ihr Unternehmen bei ChatGPT unsichtbar ist',
    seoDescription:
      'Fast 70 % der Google-Suchen enden ohne Klick. Warum der Mittelstand in KI-Antworten fehlt – und was Generative Engine Optimization (GEO) konkret bringt.',
    category: 'GEO & KI-Sichtbarkeit',
    readingTime: '8 Min.',
    datePublished: '2026-07-28',
    heroImage: '/images/ratgeber/geo-warum-unsichtbar-bei-ki',
    heroImageAlt: 'Illustration einer KI-Antwortkarte, die klassische Suchergebnisse ersetzt',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead & KI-Experte',
    },
    tags: ['GEO', 'KI-Sichtbarkeit', 'KMU', 'SEO', 'ChatGPT', 'Perplexity', 'Gemini'],
    series: {
      name: 'GEO für KMU',
      part: 1,
      total: 4,
      parts: [
        { part: 1, title: 'Das Problem: Warum Sie bei KI unsichtbar sind', slug: 'geo-warum-unsichtbar-bei-ki' },
        { part: 2, title: 'Wie ChatGPT, Perplexity & Google ihre Quellen wählen' },
        { part: 3, title: 'Praxis-Leitfaden für den Mittelstand' },
        { part: 4, title: 'Technik zum Kopieren: Schema, robots.txt, llms.txt' },
      ],
    },
    contentBlocks: [
      {
        type: 'lead',
        text:
          'Immer mehr Menschen recherchieren über KI-Systeme, die eine fertige Antwort geben – statt über eine Google-Trefferliste. Wer dort nicht genannt wird, ist für diese Kunden nicht vorhanden. Und genau das trifft heute die Mehrheit der Mittelständler, **unabhängig von Größe, Zertifizierung oder Ruf am Markt**.',
      },
      { type: 'heading', text: 'Machen Sie den Test' },
      {
        type: 'paragraph',
        text:
          'Öffnen Sie ChatGPT und stellen Sie die Frage, mit der Ihre Kunden zu Ihnen finden würden – etwa *„Welche Anbieter für [Ihre Leistung] in [Ihre Region] kannst du empfehlen?"*',
      },
      { type: 'paragraph', text: 'Werden Sie genannt? Und wenn ja: stimmt, was die KI über Sie sagt?' },
      {
        type: 'callout',
        variant: 'warning',
        title: 'Das Muster',
        text:
          'Bei den meisten Betrieben lautet die Antwort zweimal „nein". Das ist kein Einzelfall, sondern ein Muster – und pro KI-Antwort werden im Schnitt nur **1 bis 3 Quellen** genannt. Um diese wenigen Plätze wird hart gerungen.',
      },
      { type: 'heading', text: 'Die Zahlen für den deutschsprachigen Mittelstand' },
      { type: 'paragraph', text: 'Mehrere Untersuchungen aus 2025 und 2026 zeichnen ein deutliches Bild:' },
      {
        type: 'list',
        items: [
          'In einer Auswertung von **960 KI-Abfragen** über 24 Mittelständler und vier KI-Systeme (kmugeo.de, 2026) lief **93 %** der Anfragen, ohne dass das passende Unternehmen genannt wurde. In einer früheren Reihe erschienen **42 %** der Firmen bei *keiner einzigen* von 40 Testfragen.',
          'Noch heikler als das Schweigen sind die Falschaussagen: In einer empirischen Untersuchung von März bis April 2026 (Marketing hfw) **erfand ChatGPT bei 96 %** der geprüften Mittelständler schlicht den Namen des Geschäftsführers.',
          'Ein Drehteilhersteller aus Baden-Württemberg – **90 Jahre am Markt, IATF-16949-zertifiziert, mehrere DAX-Konzerne als Kunden** – erreicht bei ChatGPT einen Sichtbarkeitswert von **null Prozent**. Bei Gemini, Perplexity und Copilot dasselbe Bild, obwohl das Unternehmen bei Google für seine Kernbegriffe sauber auf den vorderen Plätzen steht.',
        ],
      },
      {
        type: 'paragraph',
        text:
          'Die unbequeme Erkenntnis: **Ruf am Markt schützt nicht.** Etablierte, erfolgreiche Betriebe fallen genauso durch – oft, weil ihre Stärken für eine Maschine schlicht nicht auffindbar hinterlegt sind.',
      },
      { type: 'heading', text: 'Warum das gerade jetzt zählt' },
      {
        type: 'paragraph',
        text:
          'Das Verhalten der Nutzer kippt messbar. Die methodisch strengste Zahl kommt vom Pew Research Center (2025), das **68.879 echte Suchen** ausgewertet hat: Erscheint eine KI-Antwort, klicken nur **8 %** auf ein klassisches Ergebnis – gegenüber **15 %** ohne. Nur **1 %** klickt auf eine Quelle *innerhalb* der Antwort. Und **26 %** beenden ihre Sitzung danach ganz: Die Antwort hat genügt.',
      },
      {
        type: 'paragraph',
        text:
          'Der Trend verstärkt sich. Anfang 2026 endeten laut SparkToro/Similarweb rund **68 %** aller Google-Suchen ohne einen einzigen Klick; der Anteil der Suchen mit Klick ist seit 2024 um fast **23 %** gefallen. KI-Antworten erscheinen inzwischen bei über einem Fünftel aller Suchen – und die Platzierung ganz oben verliert dann rund **58 %** ihrer Klicks (Ahrefs, 2026).',
      },
      {
        type: 'callout',
        variant: 'info',
        title: 'Anders gesagt',
        text: 'Die KI-Antwort *ist* zunehmend das Ergebnis. Sichtbarkeit heißt heute, **in der Antwort genannt zu werden** – nicht darunter verlinkt.',
      },
      {
        type: 'image',
        base: '/images/ratgeber/geo-seo-vs-geo',
        alt: 'Vergleich: klassische Trefferliste links, hervorgehobene KI-Antwortkarte mit Zitat rechts',
        caption: 'SEO liefert eine Liste zum Klicken – GEO landet in der einen Antwort, die zählt.',
      },
      { type: 'heading', text: 'Wenige Besucher, aber die richtigen' },
      {
        type: 'paragraph',
        text:
          'Wer über eine KI-Antwort zu Ihnen findet, hat sich schon informiert und kommt mit klarer Absicht. Anbieterzahlen deuten darauf hin, dass solche Besucher deutlich häufiger zu Kunden werden als über die klassische Suche (Seer Interactive, 2025: rund 16 % aus ChatGPT gegenüber knapp 2 % über die organische Suche – als Größenordnung, nicht als Gesetz). Die Menge der KI-Besucher ist heute klein, ihr Wert je Kopf aber hoch. Genau deshalb lohnt es, früh dranzubleiben: Erst **rund 40 %** der Unternehmen kümmern sich überhaupt um KI-Sichtbarkeit, nur etwa **16 %** messen sie. Wer jetzt anfängt, hat einen Vorsprung, der sich mit der Zeit vergrößert.',
      },
      { type: 'heading', text: 'Was ist GEO – und wie unterscheidet es sich von SEO?' },
      {
        type: 'paragraph',
        text:
          '**Generative Engine Optimization (GEO)** ist die Aufgabe, Inhalte so aufzubereiten, dass KI-Antwortsysteme (ChatGPT, Google AI Overviews, Perplexity, Gemini, Copilot) Ihr Unternehmen *verstehen, richtig einordnen und nennen*.',
      },
      {
        type: 'table',
        headers: ['', 'Klassisches SEO', 'GEO'],
        rows: [
          ['Ziel', 'Platz in der Trefferliste', 'In der KI-Antwort genannt werden'],
          ['Ergebnis', 'Verlinkte Treffer zum Klicken', 'Fertige Antwort mit Quellen'],
          ['Erfolgsmaß', 'Platzierung & Klicks', 'Nennungsrate & Richtigkeit'],
        ],
      },
      {
        type: 'paragraph',
        text:
          'Wichtig: Eine gute Google-Platzierung sichert die KI-Nennung **nicht** mehr. Weniger als 10 % der von KI zitierten Quellen stehen bei Google unter den ersten zehn Treffern (mehrere Auswertungen 2026). Die gute Nachricht bleibt: GEO ist kein Bruch mit SEO, sondern ein Aufbau darauf. Schnelle Ladezeit, saubere Struktur und Auffindbarkeit nützen beidem. Wer SEO ernst genommen hat, startet mit Vorsprung.',
      },
      { type: 'heading', text: 'Drei Dinge, die Sie schon diese Woche tun können' },
      {
        type: 'list',
        ordered: true,
        items: [
          '**Ausgangsmessung machen:** 20 typische Kundenfragen in ChatGPT, Gemini und Perplexity stellen und festhalten – genannt? richtig?',
          '**Google-Unternehmensprofil prüfen:** vollständig, richtig, aktuell? (Warum das im deutschsprachigen Raum so zentral ist, kommt in Teil 3.)',
          '**Aussagen belegbar machen:** Zahlen und Behauptungen auf Ihrer Website mit klaren Quellen versehen – der stärkste erwiesene GEO-Hebel.',
        ],
      },
      { type: 'heading', text: 'Fragen und Antworten' },
      {
        type: 'faq',
        items: [
          {
            question: 'Ersetzt GEO das klassische SEO?',
            answer:
              'Nein. GEO baut auf SEO auf. Schnelle Ladezeit, saubere Struktur und Auffindbarkeit nützen beidem. Wer SEO auslässt, kann GEO nicht abkürzen.',
          },
          {
            question: 'Ist das nicht nur ein kurzlebiger Hype?',
            answer:
              'Das Feld ist jung, und viele Prozentzahlen stammen von Anbietern – Vorsicht gegenüber exakten Werten ist berechtigt. Der grundlegende Wandel im Verhalten (KI-Antwort statt Trefferliste) ist dagegen unabhängig belegt, am klarsten durch die Pew-Daten.',
          },
          {
            question: 'Warum wird mein etabliertes Unternehmen nicht genannt?',
            answer:
              'Weil KI-Systeme auf maschinenlesbare, einheitliche und belegte Angaben angewiesen sind. Ein Ruf, der nur „am Markt bekannt" ist, aber nirgends geordnet im Web steht, ist für sie nicht sichtbar.',
          },
          {
            question: 'Wie messe ich, ob GEO wirkt?',
            answer:
              'Über wiederkehrende Testfragen in ChatGPT, Gemini, Perplexity und Claude – z. B. „Wer bietet Steuerberatung in Wernigerode an?". Wichtig sind: Werden wir genannt? Sind die Angaben korrekt? Wird auf uns verlinkt? Ergänzend hilft die Auswertung der Referrer aus KI-Tools in Analytics.',
          },
          {
            question: 'Welche konkreten Maßnahmen bringen bei GEO am meisten?',
            answer:
              'Klar strukturierte Antworten direkt am Anfang jeder Seite, belegte Zahlen und Quellen, FAQ-Blöcke mit natürlichen Fragen, saubere Schema.org-Daten (LocalBusiness, FAQPage, Service) und ein aktueller, konsistenter Google-Unternehmens­eintrag.',
          },
          {
            question: 'Wie lange dauert es, bis GEO-Maßnahmen wirken?',
            answer:
              'Erste Effekte in KI-Antworten sind oft bereits nach wenigen Wochen sichtbar, sobald die Modelle neuere Crawls oder Live-Suchen nutzen. Nachhaltige Erwähnungen mit Verlinkung entstehen typischerweise über 3–6 Monate – parallel zur klassischen SEO-Entwicklung.',
          },
        ],
      },
      {
        type: 'cta',
        title: 'GEO-Check für Ihr Unternehmen',
        text: 'Sie wollen wissen, ob und wie ChatGPT, Gemini & Co. Sie heute nennen? Wir prüfen Ihre KI-Sichtbarkeit und leiten die nächsten Schritte ab.',
        label: 'Gespräch vereinbaren',
        to: '/kontakt',
      },
    ],
    sources: [
      {
        label: 'Aggarwal et al., GEO: Generative Engine Optimization, KDD 2024',
        url: 'https://arxiv.org/abs/2311.09735',
        note: 'Peer-reviewed',
      },
      { label: 'Pew Research Center 2025 – Auswertung von 68.879 Suchen', note: 'unabhängig' },
      { label: 'SparkToro / Similarweb 2026 – Zero-Click-Analyse' },
      { label: 'Ahrefs 2026 – Auswirkung von AI Overviews auf Klickraten' },
      { label: 'kmugeo.de 2026 – 960 KI-Abfragen zu 24 Mittelständlern', note: 'Anbieterstudie' },
      { label: 'Marketing hfw 2026 – Falschaussagen von ChatGPT über KMU', note: 'Anbieterstudie' },
      { label: 'Seer Interactive 2025 – Conversion-Vergleich ChatGPT vs. organische Suche', note: 'Anbieterstudie' },
    ],
    relatedServices: ['seo-optimierung', 'geo', 'ki-beratung'],
  },
  {
    slug: 'kilanka-einfuehrung-pasapa-fachsoftware',
    title: 'Kilanka erfolgreich eingeführt: Wie wir bei Pasapa Mensch und Beruf e.V. eine Eigenentwicklung durch eine moderne Fachsoftware ersetzt haben',
    excerpt:
      'In rund sechs Monaten haben wir pasapa Mensch und Beruf e.V. von einer auslaufenden Eigenentwicklung auf die Fachsoftware Kilanka migriert – von Prozessanalyse über Datenmigration bis zum Go-live ohne Betriebsunterbrechung.',
    seoTitle: 'Kilanka Einführung bei Pasapa: Fachsoftware für soziale Träger',
    seoDescription: 'Wie wir pasapa Mensch und Beruf e.V. in 6 Monaten auf die Fachsoftware Kilanka migriert haben – Prozessanalyse, Datenmigration, Go-live ohne Ausfall.',
    category: 'Digitalisierung',
    readingTime: '11 Min.',
    datePublished: '2026-06-30',
    heroImage: '/images/ratgeber/kilanka-fachsoftware',
    heroImageAlt: 'Team einer sozialen Einrichtung arbeitet gemeinsam an einem Laptop mit neuer Fachsoftware',
    cardImagePosition: '50% 45%',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead & KI-Experte',
    },
    tags: ['Digitalisierung', 'Fachsoftware', 'Kilanka', 'Soziale Einrichtungen', 'Datenmigration', 'Prozessberatung', 'Harz'],
    sections: [
      {
        heading: 'Warum eine neue Fachsoftware mehr ist als ein IT-Projekt',
        paragraphs: [
          'Die Einführung einer neuen Fachsoftware ist kein IT-Projekt von der Stange. Gerade in sozialen Einrichtungen greifen Dokumentation, Personalverwaltung, Dienstplanung und gesetzliche Vorgaben ineinander. Entsprechend hoch sind die Anforderungen an eine Software – und an deren Einführung.',
          'Als Agentur begleiten wir regelmäßig Unternehmen und Organisationen bei Digitalisierungsprojekten. Dabei geht es selten nur um die Installation einer Anwendung. Viel wichtiger ist es, bestehende Arbeitsabläufe zu verstehen, bewährte Prozesse zu erhalten und gleichzeitig die Möglichkeiten moderner Software sinnvoll zu nutzen.',
          'Genau diese Aufgabe stellte sich auch bei unserem Projekt mit Pasapa Mensch und Beruf e.V.. Innerhalb von rund sechs Monaten haben wir den Träger bei der Einführung der Fachsoftware Kilanka begleitet – von der ersten Bestandsaufnahme über die Datenmigration bis zum erfolgreichen Produktivstart.',
        ],
      },
      {
        heading: 'Ausgangssituation: Wenn eine Eigenentwicklung zum Bremsklotz wird',
        paragraphs: [
          'Viele soziale Einrichtungen kennen diese Situation. Über Jahre wurde eine eigene Software aufgebaut, erweitert und an die individuellen Anforderungen angepasst. Das System funktioniert, die Mitarbeitenden kennen die Abläufe und jeder weiß, an welcher Stelle welche Informationen zu finden sind.',
          'Doch irgendwann verändert sich das Umfeld. Gesetzliche Anforderungen entwickeln sich weiter, Dokumentationspflichten nehmen zu und neue Arbeitsabläufe lassen sich nur noch mit erheblichem Programmieraufwand umsetzen. Gleichzeitig fehlen regelmäßige Updates, Sicherheitsanpassungen oder neue Funktionen, die heute selbstverständlich erwartet werden.',
          'Genau an diesem Punkt befand sich Pasapa Mensch und Beruf e.V. Die vorhandene Eigenentwicklung hatte viele Jahre gute Dienste geleistet, wurde aber nicht mehr aktiv weiterentwickelt. Jede Anpassung verursachte zusätzlichen Aufwand. Die Entscheidung fiel deshalb bewusst für einen Wechsel auf eine professionelle Fachsoftware, die kontinuierlich weiterentwickelt wird und langfristig Planungssicherheit bietet.',
        ],
      },
      {
        heading: 'Bevor über Software gesprochen wird, geht es um Prozesse',
        paragraphs: [
          'Eine neue Software löst keine organisatorischen Probleme. Sie kann bestehende Abläufe unterstützen, vereinfachen oder beschleunigen – sie ersetzt jedoch keine durchdachte Organisation. Deshalb beginnen wir Projekte dieser Art nie mit der Installation einer Anwendung.',
          'Unsere erste Aufgabe bestand darin, den Arbeitsalltag bei Pasapa kennenzulernen. Gemeinsam mit den verantwortlichen Mitarbeitenden haben wir analysiert, welche Informationen täglich benötigt werden, wie Dokumentationen entstehen, welche Prozesse voneinander abhängen und an welchen Stellen die bisherige Software bereits an ihre Grenzen stößt.',
          'Diese Analyse ist häufig der wichtigste Teil eines Projekts. Wer sofort mit der Einrichtung beginnt, übernimmt oft unbemerkt alte Arbeitsweisen in ein neues System. Unser Ziel war jedoch ein anderes: Bewährte Prozesse sollten erhalten bleiben, gleichzeitig wollten wir die Möglichkeiten einer modernen Fachsoftware konsequent nutzen.',
        ],
      },
      {
        heading: 'Warum wir Kilanka empfohlen haben',
        paragraphs: [
          'Im nächsten Schritt stand die Auswahl der geeigneten Lösung im Mittelpunkt. Gesucht wurde eine Fachsoftware, die den Arbeitsalltag sozialer Einrichtungen vollständig unterstützt und gleichzeitig genügend Flexibilität für zukünftige Entwicklungen bietet. Nach der Bewertung verschiedener Möglichkeiten fiel die Entscheidung auf Kilanka.',
          'Kilanka vereint die wesentlichen Verwaltungs- und Dokumentationsprozesse in einer zentralen Plattform. Für Pasapa bedeutete das, dass künftig keine unterschiedlichen Anwendungen mehr parallel genutzt werden müssen. Berücksichtigt wurden unter anderem digitale Klientendokumentation, Mitarbeiterverwaltung, Dienstplanung, Arbeitszeiterfassung, Urlaubsverwaltung, Dokumentenmanagement und Stammdatenverwaltung.',
          'Gerade die Klientendokumentation spielt in sozialen Einrichtungen eine zentrale Rolle. Informationen müssen vollständig, nachvollziehbar und jederzeit verfügbar sein. Gleichzeitig sollen Mitarbeitende möglichst wenig Zeit mit administrativen Tätigkeiten verbringen. Die Möglichkeiten von Kilanka passen genau zu diesem Anspruch.',
        ],
      },
      {
        heading: 'Ein Projekt dieser Größenordnung braucht einen klaren Fahrplan',
        paragraphs: [
          'Aus unserer Erfahrung wissen wir, dass Softwareeinführungen selten an der Technik scheitern. Probleme entstehen meist dann, wenn Zuständigkeiten unklar sind, Daten ungeprüft übernommen werden oder Mitarbeitende erst kurz vor dem Produktivstart eingebunden werden. Deshalb haben wir gemeinsam mit Pasapa zunächst einen detaillierten Projektplan entwickelt.',
          'Für jede Projektphase wurden konkrete Ziele definiert: Welche Daten müssen aus der bisherigen Eigenentwicklung übernommen werden? Welche Informationen können archiviert werden? Welche Module werden zuerst eingerichtet? Welche Abhängigkeiten bestehen? Wann beginnt die Testphase? Welche Aufgaben müssen vor dem Produktivstart abgeschlossen sein?',
          'Dieser strukturierte Ablauf sorgte dafür, dass während der gesamten Projektlaufzeit Transparenz bestand. Gerade bei einer Einführung, die den laufenden Betrieb einer sozialen Einrichtung betrifft, schafft eine solche Planung Sicherheit – sowohl für den Kunden als auch für alle Mitarbeitenden, die später täglich mit der neuen Software arbeiten.',
        ],
      },
      {
        heading: 'Unsere Aufgabe ging weit über die technische Einrichtung hinaus',
        paragraphs: [
          'Oft wird unterschätzt, wie viele Entscheidungen zwischen der Auswahl einer Software und ihrem produktiven Einsatz getroffen werden müssen. Unsere Rolle bestand deshalb nicht darin, Kilanka lediglich zu installieren oder Standardkonfigurationen vorzunehmen. Vielmehr verstanden wir uns als Bindeglied zwischen den fachlichen Anforderungen des Kunden und den technischen Möglichkeiten der Software.',
          'Während Pasapa die täglichen Arbeitsabläufe und organisatorischen Anforderungen einbrachte, beschäftigten wir uns intensiv mit den Konfigurationsmöglichkeiten von Kilanka, prüften verschiedene Lösungswege und stimmten technische Fragestellungen direkt mit dem Support des Herstellers ab.',
          'Am Ende entscheidet nicht die Anzahl der Funktionen über den Erfolg eines Projekts. Entscheidend ist, dass die Software die Menschen unterstützt, die täglich mit ihr arbeiten.',
        ],
      },
      {
        heading: 'Von der Theorie in die Praxis: Die Einrichtung von Kilanka',
        paragraphs: [
          'Mit der Entscheidung für Kilanka begann die eigentliche Projektarbeit. Der größte Teil eines solchen Projekts besteht aus zahlreichen kleinen Entscheidungen, die später darüber entscheiden, ob Mitarbeitende effizient arbeiten können oder sich täglich mit unnötigen Umwegen beschäftigen müssen.',
          'Bevor die ersten Daten importiert wurden, haben wir sämtliche Bereiche von Kilanka gemeinsam mit den Verantwortlichen bei Pasapa durchgesprochen. Welche Informationen werden tatsächlich benötigt? Welche Felder werden täglich genutzt? Welche Dokumentationen müssen schnell auffindbar sein? Welche Rollen benötigen welche Berechtigungen?',
          'Unser Ziel war es nicht, möglichst viele Funktionen zu aktivieren. Viel wichtiger war eine Konfiguration, die zu den tatsächlichen Arbeitsabläufen passt. Eine Fachsoftware sollte Prozesse unterstützen – nicht komplizierter machen.',
        ],
      },
      {
        heading: 'Bestehende Abläufe hinterfragen, ohne Bewährtes aufzugeben',
        paragraphs: [
          'Ein Softwarewechsel bietet immer die Gelegenheit, die eigene Arbeitsweise kritisch zu betrachten. Das bedeutet jedoch nicht, alles bisherige über Bord zu werfen. Viele Abläufe hatten sich über Jahre bewährt und sollten auch künftig erhalten bleiben. Gleichzeitig gab es Prozesse, die nur deshalb entstanden waren, weil die alte Eigenentwicklung bestimmte Funktionen nicht unterstützte.',
          'An einigen Stellen konnten Arbeitsschritte vollständig entfallen. Andere Prozesse wurden zusammengeführt oder durch die Möglichkeiten der Software deutlich vereinfacht. Moderne Fachsoftware ersetzt nicht einfach eine alte Anwendung – sie eröffnet häufig ganz neue Möglichkeiten, Arbeitsabläufe effizienter zu gestalten.',
        ],
      },
      {
        heading: 'Datenmigration: Mehr als ein Export aus der alten Datenbank',
        paragraphs: [
          'Einer der anspruchsvollsten Projektabschnitte war die Übernahme der vorhandenen Daten. Da die bisherige Lösung individuell entwickelt worden war, existierten keine fertigen Importwerkzeuge oder Standardschnittstellen. Die Informationen lagen in einer Datenbank vor, deren Struktur über viele Jahre gewachsen war.',
          'Für uns begann die eigentliche Datenmigration deshalb nicht mit einem Import, sondern mit einer Analyse. Gemeinsam mit Pasapa haben wir zunächst festgelegt, welche Informationen künftig weiterhin benötigt werden und welche Daten ausschließlich historischen Charakter besitzen. Nicht alles, was über Jahre gespeichert wurde, muss zwangsläufig in ein neues System übernommen werden.',
          'Anschließend wurden die relevanten Daten exportiert, aufbereitet und den Strukturen von Kilanka zugeordnet. Erst nach Prüfung und Testimport erfolgte die eigentliche Übernahme. Dieses schrittweise Vorgehen reduziert Fehler erheblich und sorgt dafür, dass Mitarbeitende vom ersten Arbeitstag an mit konsistenten Daten arbeiten können.',
        ],
      },
      {
        heading: 'Warum Testläufe und der direkte Draht zum Support unverzichtbar sind',
        paragraphs: [
          'Bei einer Softwareeinführung gibt es keinen zweiten ersten Eindruck. Deshalb führen wir vor jedem Produktivstart mehrere Testphasen durch. Auch bei Pasapa wurden Datenimporte, Arbeitsabläufe und Berechtigungen zunächst in einer Testumgebung überprüft. Gemeinsam mit den späteren Anwenderinnen und Anwendern konnten typische Alltagssituationen nachgestellt werden.',
          'Kein Softwareprojekt verläuft vollständig ohne Rückfragen. Während des gesamten Projekts standen wir in regelmäßigem Kontakt mit dem Support von Kilanka. Statt selbst zwischen Hersteller und Projektbeteiligten vermitteln zu müssen, übernahmen wir die technische Abstimmung und sorgten dafür, dass offene Punkte zeitnah gelöst wurden.',
        ],
      },
      {
        heading: 'Mitarbeitende frühzeitig einbinden',
        paragraphs: [
          'Die beste Software nützt wenig, wenn sie im Arbeitsalltag nicht akzeptiert wird. Aus diesem Grund beziehen wir die späteren Nutzerinnen und Nutzer möglichst früh in das Projekt ein. Während der Einrichtung konnten Mitarbeitende ihre Erfahrungen aus dem täglichen Arbeitsablauf einbringen – viele dieser Hinweise flossen unmittelbar in die weitere Konfiguration ein.',
          'Diese Vorgehensweise verbessert die Qualität der Lösung und sorgt dafür, dass die Einführung nicht als Bruch wahrgenommen wird, sondern als nachvollziehbare Weiterentwicklung der bisherigen Arbeitsweise.',
        ],
      },
      {
        heading: 'Ein geplanter Go-live statt eines hektischen Systemwechsels',
        paragraphs: [
          'Je näher der Produktivstart rückt, desto größer wird häufig die Anspannung. Schließlich hängt ein großer Teil der täglichen Arbeit von einer funktionierenden Software ab. Durch die sorgfältige Vorbereitung verlief der Wechsel bei Pasapa jedoch planmäßig. Daten waren übernommen, die Konfiguration abgeschlossen und Arbeitsabläufe in der Testphase erprobt. Kilanka konnte ohne unnötige Unterbrechungen in den laufenden Betrieb integriert werden.',
          'Auch nach dem Go-live endete unsere Arbeit nicht. Gerade in den ersten Wochen nach einer Einführung entstehen erfahrungsgemäß neue Fragen oder Optimierungswünsche. Deshalb standen wir dem Kunden weiterhin als Ansprechpartner zur Verfügung und begleiteten die ersten Schritte im produktiven Betrieb.',
        ],
      },
      {
        heading: 'Was sich nach der Einführung verändert hat',
        paragraphs: [
          'Der Erfolg einer Softwareeinführung lässt sich nicht daran messen, ob am Ende alle Module eingerichtet sind. Entscheidend ist die Frage, ob die neue Lösung den Arbeitsalltag tatsächlich verbessert. Einige Wochen nach dem Produktivstart zeigte sich bei Pasapa, dass genau dieses Ziel erreicht worden war.',
          'Besonders bei der Klientendokumentation machte sich der Unterschied bemerkbar. Informationen müssen nicht mehr an verschiedenen Stellen gesucht oder mehrfach erfasst werden. Auch in Mitarbeiterverwaltung, Dienstplanung sowie Arbeitszeit- und Urlaubsverwaltung profitieren die Verantwortlichen heute von einer einheitlichen Datenbasis. Das reduziert Abstimmungsaufwand und sorgt dafür, dass Informationen nicht mehr in verschiedenen Systemen gepflegt werden müssen.',
        ],
      },
      {
        heading: 'Unsere Erfahrungen aus dem Projekt',
        paragraphs: [
          'Jede Einrichtung ist anders organisiert. Trotzdem gibt es Erkenntnisse, die sich durch nahezu jedes Einführungsprojekt ziehen. Die erste lautet: Eine gute Vorbereitung spart später erheblich Zeit. Viele Schwierigkeiten lassen sich bereits in der Planungsphase vermeiden.',
          'Die zweite Erkenntnis betrifft die Datenmigration. Eine neue Fachsoftware ist eine gute Gelegenheit, historisch gewachsene Datenbestände zu bereinigen und mit einer sauberen Struktur neu zu starten. Ebenso wichtig ist die Kommunikation: Je früher Fragen rund um neue Abläufe, Sichtbarkeit von Informationen und Erfassungsaufwand beantwortet werden, desto größer ist die Akzeptanz innerhalb der Organisation.',
        ],
      },
      {
        heading: 'Unser Fazit – und Ihr nächster Schritt',
        paragraphs: [
          'Die Einführung von Kilanka bei Pasapa Mensch und Beruf e.V. war kein Projekt, das mit der Installation einer Software begann oder mit dem Produktivstart endete. Über rund sechs Monate haben wir den gesamten Veränderungsprozess begleitet – von der Analyse über Planung, Migration und Einrichtung bis zur Unterstützung nach dem Go-live.',
          'Rückblickend bestätigt dieses Projekt unsere Überzeugung: Erfolgreiche Digitalisierung entsteht nicht durch Software allein. Sie entsteht dort, wo Menschen, Prozesse und Technik sinnvoll zusammengeführt werden.',
          'Sie stehen vor einem ähnlichen Schritt – sei es eine Kilanka-Einführung, die Ablösung einer Eigenentwicklung oder die Optimierung einer bestehenden Fachsoftware? Sprechen Sie uns an. Über unser Kontaktformular vereinbaren wir gerne ein unverbindliches Erstgespräch und schauen gemeinsam, wie wir Ihre Organisation begleiten können.',
        ],
      },
    ],
    relatedServices: ['digitalisierung-beratung'],
  },
  {
    slug: 'ki-agentensysteme-automatisierung-lokale-modelle',
    title: 'KI-Agentensysteme: Wie autonome Agenten Routineaufgaben übernehmen – und worauf KMU achten müssen',
    excerpt:
      'KI entwickelt sich vom Chatbot zum autonomen Agenten, der im Hintergrund Aufgaben erledigt. Welche Chancen das für KMU im Harz bietet, welche Risiken bei Datenschutz und Kosten zu beachten sind und warum lokale Modelle eine sinnvolle Ergänzung zu Claude und Gemini sind.',
    category: 'Künstliche Intelligenz',
    readingTime: '9 Min.',
    datePublished: '2026-05-31',
    heroImage: '/images/ratgeber/ki-agentensysteme',
    heroImageAlt: 'Abstrakte Darstellung vernetzter KI-Agenten mit orangefarbenen Datenflüssen',
    cardImagePosition: '50% 50%',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead & KI-Experte',
    },
    tags: ['KI', 'KI-Agenten', 'Automatisierung', 'Datenschutz', 'Lokale KI', 'Claude', 'Gemini', 'Ollama', 'KMU', 'Harz'],
    sections: [
      {
        heading: 'Von Chatbots zu Agenten – der nächste Entwicklungsschritt',
        paragraphs: [
          'In den letzten zwei Jahren haben sich Sprachmodelle wie Claude, Gemini, ChatGPT oder Copilot von Microsoft vom Spielzeug für neugierige Anwender zu festen Werkzeugen im Arbeitsalltag entwickelt. Der nächste Schritt geht aber deutlich weiter: KI-Agentensysteme arbeiten nicht mehr nur Frage-Antwort-orientiert, sondern übernehmen mehrstufige Aufgaben eigenständig im Hintergrund.',
          'Ein Agent kann zum Beispiel eine E-Mail lesen, daraus eine Rechnung extrahieren, sie im Buchhaltungssystem anlegen, eine Antwort vorbereiten und das Ergebnis dem Mitarbeitenden zur Freigabe vorlegen – ohne dass jemand für jeden einzelnen Schritt einen Prompt schreiben muss. Das unterscheidet Agenten klar von klassischen Chatbots: Sie planen, nutzen Tools und führen Aktionen aus.',
        ],
      },
      {
        heading: 'Wo Agenten heute schon Mehrwert liefern',
        paragraphs: [
          'Gerade kleine und mittlere Unternehmen im Harz haben oft das Problem, dass die spannenden Aufgaben in einer Flut aus wiederkehrender Routine untergehen: Angebote raussuchen, E-Mails sortieren, CRM-Einträge pflegen, Berichte zusammenfassen, Inhalte recherchieren. Genau hier spielen Agentensysteme ihre Stärke aus.',
          'Typische Einsatzfelder sind die Vorqualifizierung von Anfragen, die automatische Erstellung von Protokollen und Berichten, die Pflege von Stammdaten in CRM- oder ERP-Systemen, die Recherche von Marktinformationen sowie die Vorbereitung von Kundensupport-Antworten. Das spart Zeit, reduziert Fehler und verschafft Mitarbeitenden Luft für die Aufgaben, die wirklich Wertschöpfung erzeugen.',
        ],
      },
      {
        heading: 'Security und Datenschutz – die Schattenseite',
        paragraphs: [
          'So attraktiv autonome Agenten sind, so ernst sind die Risiken. Ein Agent, der eigenständig E-Mails liest, Daten abruft oder in Systeme schreibt, ist im Zweifel ein Einfallstor. Stichworte sind Prompt Injection, ungewollter Datenabfluss in Cloud-Modelle und der Umgang mit personenbezogenen Daten unter DSGVO.',
          'Wer Agenten einsetzt, sollte von Anfang an mit klaren Rechten arbeiten: Welcher Agent darf auf welche Daten zugreifen, wo werden Aktionen protokolliert, welche Schritte erfordern eine menschliche Freigabe? Sandbox-Umgebungen, Audit-Logs und ein durchdachtes Berechtigungskonzept sind keine Kür, sondern Pflicht.',
        ],
      },
      {
        heading: 'Kostenfalle Cloud-KI',
        paragraphs: [
          'Ein Punkt, der häufig unterschätzt wird: KI wird tendenziell teurer, nicht günstiger. Die Anbieter der großen Modelle haben ihre Preise in den letzten Monaten mehrfach angepasst, und Agentensysteme verstärken das Problem, weil sie pro Aufgabe oft mehrere Modellaufrufe machen – planen, Tools aufrufen, Ergebnisse bewerten, korrigieren.',
          'Was im Pilotbetrieb mit ein paar Cents pro Vorgang harmlos wirkt, kann in der Skalierung schnell zu vier- oder fünfstelligen Monatsrechnungen werden. Ein realistisches Kostenmodell gehört deshalb von Beginn an in jede Agenten-Strategie.',
        ],
      },
      {
        heading: 'Lokale und Open-Source-Modelle als Alternative',
        paragraphs: [
          'Die gute Nachricht: Es muss nicht immer das Top-Modell aus der Cloud sein. Lokale und Open-Source-Modelle wie Llama, Mistral oder Qwen – betrieben über Werkzeuge wie Ollama – sind in den letzten Generationen erstaunlich leistungsfähig geworden. Sie erreichen nicht in jedem Benchmark das Niveau von Claude oder Gemini, sind aber für viele praxisrelevante Aufgaben absolut ausreichend.',
          'Für Klassifikation, Zusammenfassungen, Datenextraktion, einfache Recherche oder die Standardschritte eines Agenten liefern lokale Modelle solide Ergebnisse – und das mit zwei großen Vorteilen: Die Daten bleiben im eigenen Netzwerk, was DSGVO-Anforderungen massiv vereinfacht, und die Kosten werden planbar, weil nicht pro Token abgerechnet wird, sondern für Strom und Hardware.',
        ],
      },
      {
        heading: 'Hybrid-Strategie für KMU im Harz',
        paragraphs: [
          'Der pragmatische Weg ist meist ein hybrider Ansatz. Anspruchsvolle Schritte – komplexes Reasoning, multilinguale Aufgaben, sehr lange Dokumente – laufen weiterhin über Cloud-Modelle wie Claude oder Gemini. Routineaufgaben, die in Masse anfallen, übernimmt ein lokales Modell. Der Agent entscheidet anhand klarer Regeln, welches Modell für welchen Schritt zuständig ist.',
          'So entsteht eine KI-Architektur, die wirtschaftlich tragfähig ist, Datenschutz respektiert und gleichzeitig die Qualität liefert, die Kundinnen und Kunden erwarten. Der Einstieg gelingt am besten über ein klar umrissenes Pilotprojekt mit einem konkreten Use Case, statt mit einer abstrakten KI-Strategie auf der grünen Wiese zu starten.',
        ],
      },
      {
        heading: 'Lassen Sie uns sprechen',
        paragraphs: [
          'Sie überlegen, wo in Ihrem Unternehmen KI-Agenten konkret entlasten könnten – und wie Sie das datenschutzkonform und kostenbewusst umsetzen? Wir analysieren mit Ihnen gemeinsam Ihre Prozesse, identifizieren passende Use Cases und zeigen Ihnen, wann Cloud-Modelle Sinn ergeben und wann ein lokales Modell die bessere Wahl ist.',
          'Vereinbaren Sie ein unverbindliches Erstgespräch über unser Kontaktformular – wir melden uns kurzfristig bei Ihnen zurück und besprechen, welcher erste Schritt in Ihrem Unternehmen am meisten Wirkung entfaltet.',
        ],
      },
    ],
    relatedServices: ['ki-beratung', 'digitalisierung-beratung'],
  },
  {
    slug: 'local-seo-harz-wernigerode',
    title: 'Local SEO für Harz-Unternehmen: So werden Sie in Wernigerode gefunden',
    excerpt:
      'Wie Handwerker, Hotels, Praxen und Dienstleister im Harz mit Google Business Profile, lokalen Keywords und regionalen Backlinks bei Suchen nach „Webdesign Wernigerode" & Co. sichtbar werden.',
    seoTitle: 'Local SEO Harz: In Wernigerode & Umgebung gefunden werden',
    seoDescription:
      'Local-SEO-Leitfaden für Harz-Unternehmen: Google Business Profile, lokale Keywords wie „Webdesign Wernigerode" und regionale Backlinks – konkret und Schritt für Schritt.',
    category: 'Local SEO',
    readingTime: '9 Min.',
    datePublished: '2026-07-24',
    heroImage: '/images/ratgeber/local-seo',
    heroImageAlt: 'Karte des Harzes mit Google-Markern über Wernigerode und Umgebung',
    cardImagePosition: '50% 50%',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead & SEO',
    },
    tags: ['Local SEO', 'Harz', 'Wernigerode', 'Google Business Profile', 'Regionale Sichtbarkeit', 'SEO'],
    sections: [
      {
        heading: 'Warum Local SEO im Harz besonders wirkt',
        paragraphs: [
          'Wer in Wernigerode, Halberstadt, Quedlinburg oder Blankenburg nach Dienstleistern sucht, tippt selten nur „Agentur" oder „Elektriker" ein – sondern „Webdesign Wernigerode", „Elektriker Halberstadt" oder „Steuerberater Harz". Suchanfragen wie „Webdesign Harz" (rund 30 Suchen pro Monat) und „Webdesign Wernigerode" (rund 40 Suchen pro Monat) haben zwar überschaubares Volumen, dafür aber eine enorm hohe Kaufabsicht: Wer so sucht, will jemanden aus der Region beauftragen.',
          'Genau das ist der Vorteil regionaler Suchbegriffe. Der Wettbewerb ist deutlich kleiner als bei generischen Keywords, die Klicks sind qualitativ hochwertiger, und mit einer sauberen Local-SEO-Basis lassen sich auch als kleines Unternehmen die Plätze 1–3 realistisch erreichen.',
        ],
      },
      {
        heading: 'Google Business Profile: Ihr wichtigster Hebel',
        paragraphs: [
          'Das Google Unternehmensprofil (früher Google My Business) ist die Grundlage jeder lokalen Sichtbarkeit. Es speist die Ergebnisse in Google Maps und das „Local Pack" – die drei Betriebe, die bei lokalen Suchen ganz oben mit Karte erscheinen. Wer hier nicht auftaucht, existiert für viele Kunden schlicht nicht.',
          'Wichtig sind vollständige und konsistente Angaben: exakter Firmenname, Adresse und Telefonnummer (NAP-Daten), passende Kategorien, aktuelle Öffnungszeiten, echte Fotos, eine ausführliche Beschreibung mit regionalen Begriffen (z. B. „Agentur aus Wernigerode für Kunden im gesamten Harz"), sowie regelmäßige Beiträge und Aktualisierungen.',
          'Bewertungen sind der zweite große Ranking- und Vertrauensfaktor. Bitten Sie zufriedene Kunden aktiv um eine Google-Bewertung und antworten Sie auf jede Rezension – auch auf kritische. Das signalisiert Google Aktivität und Kundennähe.',
        ],
      },
      {
        heading: 'Lokale Keywords sinnvoll in Inhalte einbauen',
        paragraphs: [
          'Damit Ihre Website für regionale Suchen rankt, brauchen Sie eigene Landingpages oder Abschnitte, die den Ort klar benennen. Statt einer allgemeinen „Leistungen"-Seite lohnt sich beispielsweise eine dedizierte Seite „Webdesign Wernigerode" oder „Webdesign Harz", die konkret auf die Region eingeht: typische Kunden vor Ort, Beispielprojekte aus dem Harz, Anfahrt, regionale Besonderheiten.',
          'Kombinieren Sie das Hauptkeyword mit Nachbarorten und Long-Tail-Varianten – etwa „Agentur Wernigerode", „Webseite erstellen Harz", „SEO Halberstadt", „Google-Optimierung Quedlinburg". Wichtig: kein Keyword-Stuffing. Ein natürlicher Text mit einer klaren Struktur (H1, aussagekräftige H2, kurze Absätze) und einer Handvoll gut platzierter Ortsnamen wirkt bei Google und bei Besuchern gleichermaßen.',
          'Ergänzen Sie strukturierte Daten (LocalBusiness-Schema) mit Adresse, Öffnungszeiten, Bewertungen und Einzugsgebiet. Damit versteht Google eindeutig, dass Sie ein regionaler Anbieter sind – und liefert Ihre Seite gezielt in lokalen Ergebnissen aus.',
        ],
      },
      {
        heading: 'Regionale Backlinks aufbauen',
        paragraphs: [
          'Backlinks aus der Region sind für Local SEO Gold wert. Sie sind für Google ein starkes Signal, dass Ihr Unternehmen lokal verankert ist. Gute Quellen im Harz sind Branchenverzeichnisse und Portale wie „Harz aktuell", die IHK Magdeburg, die Handwerkskammer Halberstadt, Wernigerode Tourismus, aber auch Vereine, Sponsoring-Partner und lokale Blogs.',
          'Wer regional als Sponsor auftritt – etwa bei Sportvereinen, Kulturveranstaltungen oder Wohltätigkeitsaktionen – erhält häufig einen Link auf der Veranstaltungsseite. Kooperationen mit anderen Unternehmen aus dem Harz (gemeinsame Beiträge, gegenseitige Empfehlungen) zahlen zusätzlich auf Ihre Autorität ein.',
          'Vermeiden Sie hingegen minderwertige Linktausch-Portale oder gekaufte Backlinks. Ein einziger Link aus einer bekannten regionalen Quelle ist mehr wert als 50 Einträge in Massenverzeichnissen.',
        ],
      },
      {
        heading: 'Technik: Mobil, schnell und konsistent',
        paragraphs: [
          'Fast alle lokalen Suchanfragen kommen vom Smartphone – oft unterwegs, oft mit „in der Nähe"-Intention. Ihre Website muss mobil einwandfrei laufen: kurze Ladezeiten, gut sichtbare Telefonnummer, Klick-to-Call, eingebettete Karte und ein einfach nutzbares Kontaktformular. Google berücksichtigt Mobile Usability und Core Web Vitals direkt im Ranking.',
          'Achten Sie zudem auf konsistente NAP-Daten (Name, Adresse, Telefon) auf allen Kanälen: eigene Website, Google-Profil, Facebook, Branchenbücher, Portale. Abweichungen führen zu Vertrauensverlust bei Google und schwächen Ihre lokale Sichtbarkeit.',
        ],
      },
      {
        heading: 'Häufig gestellte Fragen',
        paragraphs: [
          'Wie lange dauert Local SEO im Harz? Erste Bewegungen sehen wir meist nach 4–8 Wochen (Google-Profil, technische Basis), stabile Top-Rankings für Keywords wie „Webdesign Wernigerode" nach 3–6 Monaten kontinuierlicher Arbeit.',
          'Reicht ein gutes Google Business Profile aus? Für sehr enge Suchen im direkten Umkreis oft ja. Für „Webdesign Harz" und ähnliche Regionen-Keywords braucht es zusätzlich optimierte Landingpages und Backlinks.',
          'Was, wenn ich mehrere Standorte im Harz habe? Legen Sie pro Standort ein eigenes Google-Profil und eine eigene Landingpage an – mit individueller URL, Adresse und passenden Inhalten. Duplicate Content zwischen den Seiten vermeiden.',
        ],
      },
      {
        heading: 'Nächster Schritt',
        paragraphs: [
          'Sie wollen wissen, wo Sie im Harz aktuell stehen und welche schnellen Hebel Ihre lokale Sichtbarkeit steigern? Wir schauen uns Ihr Google-Profil, Ihre Website und die Wettbewerber aus Wernigerode und Umgebung an und zeigen Ihnen konkret, wo Sie ansetzen sollten.',
          'Melden Sie sich unverbindlich über unser Kontaktformular – wir vereinbaren ein kurzes Erstgespräch und leiten Ihnen anschließend einen priorisierten Local-SEO-Fahrplan zu.',
        ],
      },
    ],
    relatedServices: ['seo-optimierung', 'webseitenerstellung'],
  },
  {
    slug: 'webseite-erstellen-wernigerode',
    title: 'Webseite erstellen lassen in Wernigerode: Strategie, Design und SEO',
    excerpt:
      'Eine erfolgreiche Webseite ist mehr als gutes Design. Hier erfahren Sie, wie Strategie, Inhalte, Technik und SEO zusammenspielen, damit aus Besuchern echte Anfragen werden.',
    category: 'Webseitenerstellung',
    readingTime: '10 Min.',
    datePublished: '2026-01-05',
    heroImage: '/images/ratgeber/webseite-erstellen',
    heroImageAlt: 'Laptop mit Code-Editor und Notizbuch',
    cardImagePosition: '50% 60%',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead',
    },
    tags: ['Webseitenerstellung', 'Webdesign', 'Wernigerode', 'Harz', 'Conversion', 'SEO'],
    sections: [
      {
        heading: 'Strategie vor Design',
        paragraphs: [
          'Eine gute Website beginnt nicht mit Farben, sondern mit Klarheit. Was soll die Seite erreichen, welche Zielgruppen sollen überzeugt werden und welche konkreten Handlungen sind das Ziel? Für Unternehmen im Harz bedeutet das häufig: mehr Anfragen, bessere Sichtbarkeit bei Google und ein professioneller erster Eindruck. Eine saubere Strategie sorgt dafür, dass Design und Inhalte später zielgerichtet funktionieren und nicht nur gut aussehen.',
          'Dazu gehört auch die Positionierung. Eine Agentur aus Wernigerode sollte den regionalen Bezug sichtbar machen, ohne übertrieben zu wirken. Ein klarer Bezug zur Region stärkt Vertrauen und sorgt dafür, dass Besucher sofort erkennen, dass sie hier einen lokalen Ansprechpartner finden.',
        ],
      },
      {
        heading: 'Inhalte, die überzeugen und gefunden werden',
        paragraphs: [
          'Content ist der Kern jeder Webseite. Texte müssen verständlich sein, Nutzen vermitteln und gleichzeitig relevante Keywords enthalten. Begriffe wie „Webdesign Wernigerode“, „Agentur Harz“ oder „SEO Sachsen-Anhalt“ helfen dabei, regionale Suchanfragen abzudecken, ohne den Text künstlich wirken zu lassen.',
          'Guter Content beantwortet Fragen, schafft Vertrauen und führt den Nutzer zur nächsten Aktion. Eine klare Struktur mit starken Überschriften, kurzen Absätzen und konkreten Beispielen erhöht die Lesbarkeit und verbessert die Conversion.',
        ],
      },
      {
        heading: 'Design und User Experience',
        paragraphs: [
          'Design ist nicht nur Geschmack, sondern Führung. Eine klare visuelle Hierarchie zeigt, was wichtig ist, wo man klickt und wie man den nächsten Schritt macht. Gerade bei Dienstleistern entscheidet der erste Eindruck darüber, ob eine Anfrage überhaupt entsteht.',
          'User Experience bedeutet auch Mobilfreundlichkeit. Viele Besucher kommen über das Smartphone. Wenn die Navigation kompliziert ist oder Formulare schlecht bedienbar sind, steigen Absprünge. Eine durchdachte UX sorgt dafür, dass Nutzer mühelos zum Kontakt finden.',
        ],
      },
      {
        heading: 'Technik und Performance als Rankingfaktor',
        paragraphs: [
          'Schnelle Ladezeiten, saubere Core Web Vitals und optimierte Bilder sind Pflicht. Google bewertet technische Qualität und belohnt Websites, die stabil, schnell und mobiloptimiert sind. Für lokale Anbieter ist das besonders wichtig, weil viele Suchanfragen mobil erfolgen.',
          'Technisches SEO umfasst außerdem strukturierte Daten, saubere Meta-Tags und klare URLs. Wenn diese Bausteine stimmen, kann die Seite ihre Inhalte besser transportieren und schneller sichtbar werden.',
        ],
      },
      {
        heading: 'Vertrauen durch Referenzen und klare CTAs',
        paragraphs: [
          'Referenzen sind für viele Besucher der wichtigste Vertrauensbeweis. Zeigen Sie echte Projekte, konkrete Ergebnisse und kurze Kundenzitate. Das wirkt stärker als jede Marketingfloskel und erhöht die Anfragequote deutlich.',
          'Call-to-Actions müssen klar und sichtbar sein. „Projekt anfragen“ oder „Kostenlose Erstberatung“ funktioniert, wenn es logisch platziert ist. Je kürzer der Weg, desto höher die Conversion.',
        ],
      },
      {
        heading: 'SEO als langfristige Sichtbarkeitsbasis',
        paragraphs: [
          'SEO ist kein Extra, sondern Teil der Webseitenerstellung. Wer von Anfang an auf saubere Struktur, relevante Keywords und hochwertige Inhalte setzt, spart später Aufwand und gewinnt schneller Sichtbarkeit.',
          'Gerade in Wernigerode und im Harz ist lokale Sichtbarkeit ein Wettbewerbsvorteil. Eine Website, die SEO mitdenkt, sorgt für langfristig stabile Rankings und kontinuierliche Anfragen.',
        ],
      },
    ],
    relatedServices: ['webseitenerstellung', 'seo-optimierung'],
  },
  {
    slug: 'pflicht-widerrufsbutton-2026',
    title: 'Widerrufsbutton 2026: Neue Pflicht für Onlineshops',
    excerpt:
      'Ab 19. Juni 2026 müssen Onlineshops einen digitalen Widerrufsbutton anbieten. Was Händler jetzt wissen müssen – von gesetzliche Vorgaben über technische Anforderungen bis zu Strafen bei Nichtbeachtung.',
    category: 'E-Commerce',
    readingTime: '15 Min.',
    datePublished: '2026-02-25',
    heroImage: '/images/ratgeber/widerufsbutton',
    heroImageAlt: 'Onlineshop-Kasse mit gut sichtbarem "Widerruf" Button',
    cardImagePosition: '50% 50%',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead',
    },
    tags: ['Widerruf', 'E-Commerce', 'Onlineshop', 'Gesetz', 'Button', 'Haendlerbund'],
    sections: [
      {
        heading: 'Hintergrund und gesetzlicher Rahmen',
        paragraphs: [
          'Am 19. Juni 2026 tritt eine Änderung im Verbraucherrecht in Kraft: Onlinehändler müssen einen klar erkennbaren Widerrufsbutton in ihren Bestellprozess integrieren. Der Schritt folgt auf EU-Richtlinien zur Verbesserung der Verbraucherrechte und soll eine einfache Ausübung des Rücktrittsrechts ermöglichen.',
          'Die Pflicht betrifft grundsätzlich alle gewerblichen Onlineshops, die Waren oder Dienstleistungen an Verbraucher verkaufen. Ziel ist es, Transparenz zu schaffen und irreführende „Kleingedrucktes“-Taktiken zu verhindern.',
        ],
      },
      {
        heading: 'Wie der Button auszusehen hat',
        paragraphs: [
          'Der Widerrufsbutton muss deutlich sichtbar platziert werden – meist im Warenkorb oder auf der Bestellübersichtsseite. Er darf nicht versteckt oder optisch zurückgesetzt sein und muss mit dem Wort „Widerruf" oder einer vergleichbaren klaren Bezeichnung beschriftet sein.',
          'Beim Klick sollte der Käufer sofort darüber informiert werden, dass sein Vertrag binnen 14 Tagen widerrufen werden kann und welche Bedingungen dafür gelten. Ein zusätzliches Bestätigungsfeld oder das Wiederholen einer Checkbox ist nicht erforderlich.',
        ],
      },
      {
        heading: 'Kritik und Debatten',
        paragraphs: [
          'Der Händlerbund und andere Verbände kritisieren den Pflichtbutton als unnötig bürokratisch. In mehreren Artikeln wurde argumentiert, dass der Button für viele Händler zusätzliche technische Hürden und potenzielle Conversion-Verluste bedeutet.',
          'Einige Stimmen sprechen sogar von einem „Widerrufsbutton-Wahnsinn“. Gegner behaupten, dass bestehende Informationspflichten und das Widerrufsrecht an sich bereits ausreichend seien und der Button lediglich Betreiber verunsichere.',
        ],
      },
      {
        heading: 'Ausnahmen für kleine Shops',
        paragraphs: [
          'Nicht jeder Verkäufer muss den Button einbauen: Kleinstunternehmen mit weniger als 10 Mitarbeitern und einem Jahresumsatz unter 2 Millionen Euro sind von der Pflicht ausgenommen, solange sie ausschließlich über Plattformen wie Etsy oder Ebay anbieten.',
          'Wer jedoch einen eigenen Webshop betreibt – auch auf Baukästen wie Shopify oder WooCommerce – fällt in der Regel nicht unter diese Ausnahme und muss den Widerrufsbutton integrieren.',
        ],
      },
      {
        heading: 'Praxis: Umsetzung für Shopbetreiber',
        paragraphs: [
          'Zur technischen Umsetzung genügt oft ein Link zu einer standardisierten Widerrufsbelehrung, der als Button gestaltet ist. E-Commerce-Systeme und Plugins bereiten sich bereits vor, sodass viele Anbieter innerhalb weniger Minuten einen entsprechenden Button einfügen können.',
          'Es ist wichtig, den Button in den Testbestellungen zu prüfen und sicherzustellen, dass er in allen relevanten Browsern und auf Mobilgeräten funktioniert. Im Zweifel sollte ein Anwalt für IT-Recht oder ein rechtlicher Dienstleister konsultiert werden.',
        ],
      },
      {
        heading: 'Was Händler tun sollten',
        paragraphs: [
          'Informieren Sie sich rechtzeitig über die neue Regel und aktualisieren Sie Ihre Checkout-Seiten vor dem Stichtag. Dokumentieren Sie die Änderungen, um bei möglichen Abmahnungen belegen zu können, dass Sie der Pflicht nachgekommen sind.',
          'Nutzen Sie die Gelegenheit, um Ihre gesamten Widerrufsbedingungen zu überprüfen und transparent darzustellen. Eine klare Kommunikation kann nicht nur Abmahnungen vermeiden, sondern auch das Vertrauen Ihrer Kunden stärken.',
        ],
      },
    ],
    relatedServices: ['webseitenerstellung'],
  },

  {
    slug: 'seo-optimierung-local-seo',
    title: 'SEO-Optimierung & Local SEO: Strategien für nachhaltige Sichtbarkeit',
    excerpt:
      'SEO ist eine Langzeitinvestition in Ihre Sichtbarkeit. Erfahren Sie, wie regionale Dienstleister mit Local SEO und technischen Optimierungen dauerhaft gefunden werden.',
    category: 'SEO-Optimierung',
    readingTime: '14 Min.',
    datePublished: '2026-02-06',
    heroImage: '/images/ratgeber/local-seo',
    heroImageAlt: 'SEO-Notizen neben einem Laptop',
    cardImagePosition: '50% 55%',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead',
    },
    tags: ['SEO-Optimierung', 'Local SEO', 'Google Business', 'Wernigerode', 'Harz', 'Rankings', 'Suchmaschinenoptimierung'],
    sections: [
      {
        heading: 'Warum SEO für Unternehmen essentiell ist',
        paragraphs: [
          'SEO ist eine der wichtigsten Investitionen für langfristige Sichtbarkeit. Im Gegensatz zu bezahlter Werbung generiert organischer Traffic kontinuierlich Anfragen, ohne für jeden Klick zu zahlen. Ein Unternehmen, das auf Seite 1 von Google rankt, bekommt täglich potenzielle Kunden – völlig automatisiert.',
          'Für Dienstleister im Harz und darüber hinaus ist dieser Effekt besonders wertvoll. Wenn jemand nach „SEO Optimierung Wernigerode" oder „Webdesign Harz" sucht, ist diese Person bereits hochqualifiziert und kaufnah. Wer hier oben rankt, gewinnt kontinuierlich neue Anfragen.',
        ],
      },
      {
        heading: 'Local SEO: Sichtbarkeit für regionale Dienstleister',
        paragraphs: [
          'Local SEO beginnt mit einer klaren Positionierung: Wer ist Ihr Angebot, wo findet es statt und warum sind Sie lokal relevant? Google bewertet Inhalte immer stärker nach Kontext, Standort und Nutzerintention. Eine Agentur in Wernigerode sollte deshalb nicht nur allgemein über Webdesign sprechen, sondern konkret zeigen, dass sie Unternehmen im Harz und in Sachsen-Anhalt begleitet.',
          'Die Basis ist ein sauberes Zusammenspiel aus Website, Google Business Profil und konsistenten Unternehmensdaten. Wenn überall dieselben Adressdaten, Öffnungszeiten und Kontaktdaten sichtbar sind, erkennt Google die Vertrauenswürdigkeit. Gerade bei lokalen Dienstleistungen ist diese Datenkonsistenz ein entscheidender Faktor für stabile Rankings.',
        ],
      },
      {
        heading: 'Google Business Profil als starke Signalquelle',
        paragraphs: [
          'Das Google Business Profil ist die wichtigste lokale Datenquelle. Es entscheidet darüber, ob Sie im Local Pack auftauchen und ob Nutzer Sie in der Nähe finden. Ein vollständig gepflegtes Profil mit echten Bildern, passenden Kategorien, aktuellen Öffnungszeiten und klaren Leistungen wirkt wie eine digitale Visitenkarte.',
          'Auch Aktivität zählt. Regelmäßige Beiträge mit Projekten, Tipps oder Updates zeigen Google, dass das Unternehmen lebt. Diese Signale wirken sich direkt auf die Sichtbarkeit aus, besonders bei Suchanfragen mit lokalem Bezug.',
        ],
      },
      {
        heading: 'Suchintention: Das Fundament jeder SEO-Strategie',
        paragraphs: [
          'Nicht alle Keywords sind gleich. Google unterscheidet zwischen Informations-, Navigations- und Transaktions-Intent. Eine Suche nach „Webdesign Agentur" unterscheidet sich fundamental von „Webdesign Tipps". Die erste Person sucht einen Anbieter, die zweite möchte lernen.',
          'Erfolgreiche SEO-Optimierung beginnt mit dem Verstehen dieser Intentionen. Die Strategie sollte aus mehreren Content-Kategorien bestehen: Informative Artikel (Ratgeber und Guides), Landingpages für kommerzielle Keywords und eine klare Servicedarstellung. So deckt man alle Phasen der Customer Journey ab.',
        ],
      },
      {
        heading: 'On-Page-SEO: Basis der Suchmaschinenfreundlichkeit',
        paragraphs: [
          'On-Page-SEO sind alle Optimierungen, die Sie direkt auf Ihrer Webseite vornehmen können. Dazu gehören Keyword-Platzierung in Title-Tags, Meta-Descriptions, Überschriften und im Body-Text. Ein gutes Title-Tag muss aussagekräftig sein, den primären Nutzen vermitteln und unter 60 Zeichen bleiben.',
          'Die interne Verlinkung ist oft unterschätzt. Durch strategisches Linken zwischen semantisch verwandten Seiten signalisieren Sie Google die Struktur Ihrer Seite und verteilen Ranking-Kraft. Verlinken Sie lokale Inhalte auf lokale Leistungen und stärken Sie die Kontaktseite.',
        ],
      },
      {
        heading: 'Technisches SEO und Performance',
        paragraphs: [
          'Technisches SEO ist die Grundlage, auf der alles andere aufbaut. Wenn die Technik nicht stimmt, können auch die besten Inhalte nicht ranken. Dazu gehören schnelle Ladezeiten, HTTPS-Verschlüsselung, mobile Optimierung und eine saubere Webseitenstruktur.',
          'Core Web Vitals sind Googles Metriken für Nutzererlebnis: Largest Contentful Paint (LCP), First Input Delay (FID) und Cumulative Layout Shift (CLS). Websites mit schlechten Web Vitals verlieren Rankings. Eine klare Seitenstruktur hilft zusätzlich und führt Nutzer schnell zur Kontaktaufnahme.',
        ],
      },
      {
        heading: 'Content-Strategie: Qualität statt Quantität',
        paragraphs: [
          'Google belohnt Websites mit einzigartigen, hilfreichen Inhalten, die Nutzerfragen umfassend beantworten. Ein 300-Wort-Artikel rankt nicht gegen einen 2.000-Wort-Guide, der tatsächlich das Thema durchdringt. Quantität allein reicht nicht – der Content muss auch besser sein als die aktuelle Konkurrenz.',
          'E-E-A-T ist Googles Bewertungskriterium: Experience, Expertise, Authoritativeness, Trustworthiness. Wer schreibt den Artikel? Ist der Autor ein anerkannter Experte? Für technische Artikel sollte ein Entwickler schreiben. Das signalisiert Expertise.',
        ],
      },
      {
        heading: 'Bewertungen und echte Referenzen',
        paragraphs: [
          'Bewertungen sind ein starker Rankingfaktor und gleichzeitig ein Vertrauenssignal. Ein einfacher Prozess, der nach jedem Projekt um Feedback bittet, sorgt langfristig für stabile Sichtbarkeit. Mehr Bewertungen bedeuten bessere Positionen und wirken direkt auf die Entscheidung potenzieller Kunden.',
          'Referenzen sind der zweite zentrale Vertrauenshebel. Eine Seite mit lokalen Projekten, klaren Ergebnissen und konkreten Branchenbeispielen zeigt Kompetenz. Wer in Wernigerode oder im Harz sucht, möchte sehen, dass Sie dort bereits erfolgreich gearbeitet haben.',
        ],
      },
      {
        heading: 'Monitoring und Datenanalyse',
        paragraphs: [
          'Ohne Daten kann man SEO nicht bewerten. Google Search Console zeigt, welche Keywords Sie ranken, auf welcher Position und wie hoch die Klickrate ist. Diese Daten sind kostenlos und essentiell für jede SEO-Strategie.',
          'Ein monatliches Monitoring ist essentiell. Regelmäßiges Reporting dokumentiert Fortschritt und validiert die Investition. SEO braucht Zeit – realistische Timeframes sind 3–6 Monate für erste Verbesserungen, 6–12 Monate für signifikante Erfolge.',
        ],
      },
      {
        heading: 'SEO ist Langzeitinvestition',
        paragraphs: [
          'SEO-Optimierung braucht Zeit, aber die Ergebnisse sind nachhaltig. Im Gegensatz zu Google Ads generiert ein gutes Ranking noch Jahre später Traffic – ohne zusätzliche Kosten. Wer in SEO investiert, baut sich einen langfristigen Wettbewerbsvorteil auf.',
          'Die beste Zeit, mit SEO zu starten, war gestern. Die zweitbeste Zeit ist heute. Je früher Sie beginnen, desto eher werden die Ergebnisse sichtbar. Eine durchdachte Strategie, konsistente Umsetzung und regelmäßiges Monitoring machen den Unterschied.',
        ],
      },
    ],
    relatedServices: ['seo-optimierung', 'webseitenerstellung'],
  },
  {
    slug: 'karriereseiten-bewerber-gewinnen',
    title: 'Karriereseiten, die Bewerber gewinnen',
    excerpt:
      'Eine Karriereseite ist der wichtigste digitale Kontaktpunkt für Bewerber. So entsteht Vertrauen, bessere Bewerbungen und eine stärkere Arbeitgebermarke.',
    category: 'Karriereseiten',
    readingTime: '9 Min.',
    datePublished: '2025-10-12',
    heroImage: '/images/ratgeber/karriereseiten',
    heroImageAlt: 'Teammeeting am Laptop',
    cardImagePosition: '50% 22%',
    author: {
      name: 'Enrico Kerwel',
      title: 'Creative Director',
    },
    tags: ['Karriereseite', 'Employer Branding', 'Recruiting', 'Webdesign'],
    sections: [
      {
        heading: 'Positionierung als Arbeitgeber',
        paragraphs: [
          'Eine Karriereseite entscheidet, ob ein Unternehmen attraktiv wirkt oder austauschbar. Bewerber möchten Werte, Kultur und Haltung sehen. Je klarer die Positionierung, desto höher die Qualität der Bewerbungen.',
          'Gerade im regionalen Umfeld wie Wernigerode oder dem Harz wirkt Authentizität stärker als Hochglanz. Eine ehrliche Darstellung schafft Vertrauen und sorgt dafür, dass sich passende Kandidaten angesprochen fühlen.',
        ],
      },
      {
        heading: 'Rollen und Aufgaben verständlich erklären',
        paragraphs: [
          'Bewerber möchten wissen, was sie erwartet. Eine klare Beschreibung der Aufgaben und Ziele reduziert Missverständnisse und erhöht die Passgenauigkeit. Ein guter Text gibt einen realistischen Einblick in den Arbeitsalltag.',
          'Vermeiden Sie überlange Anforderungskataloge. Ein fokussierter Text mit den wichtigsten Skills wirkt einladender und senkt die Absprungrate.',
        ],
      },
      {
        heading: 'Einblicke in Team und Alltag',
        paragraphs: [
          'Menschen bewerben sich bei Menschen. Fotos, kurze Statements oder Projektbeispiele schaffen Nähe. Sie machen die Unternehmenskultur greifbar und erhöhen die Bindung.',
          'Professionelles Webdesign unterstützt diese Wirkung. Eine klare Struktur, gute Bildsprache und ein moderner Look vermitteln Qualität und stärken die Arbeitgebermarke.',
        ],
      },
      {
        heading: 'Benefits und Entwicklungschancen',
        paragraphs: [
          'Benefits wirken nur, wenn sie konkret sind. Flexible Arbeitszeiten, Weiterbildung oder Homeoffice müssen greifbar beschrieben werden. Das erzeugt Klarheit und hilft Bewerbern, das Angebot einzuordnen.',
          'Weniger ist oft mehr. Eine übersichtliche Darstellung der wichtigsten Vorteile wirkt stärker als eine lange Liste und bleibt besser im Kopf.',
        ],
      },
      {
        heading: 'Bewerbungsprozess einfach halten',
        paragraphs: [
          'Der Bewerbungsprozess darf nicht kompliziert sein. Je einfacher der Weg, desto höher die Abschlussrate. Kurze Formulare oder eine Bewerbung per E-Mail reichen oft aus, besonders bei lokalen Unternehmen.',
          'Mobile Optimierung ist Pflicht. Viele Bewerber informieren sich unterwegs. Wenn die Karriereseite nicht mobil funktioniert, gehen Kandidaten verloren.',
        ],
      },
      {
        heading: 'SEO und lokale Sichtbarkeit',
        paragraphs: [
          'Karriereseiten müssen auffindbar sein. Keywords wie „Jobs Wernigerode“, „Karriere Harz“ oder „Ausbildung Sachsen-Anhalt“ sollten in Titel und Texten erscheinen, ohne künstlich zu wirken.',
          'Local SEO sorgt dafür, dass Bewerber aus der Region die Stellenangebote schnell finden. Damit wird die Karriereseite nicht nur ein Branding-Instrument, sondern auch ein Recruiting-Kanal.',
        ],
      },
    ],
    relatedServices: ['karriereseiten', 'webseitenerstellung'],
  },
  {
    slug: 'hosting-betrieb-schnelle-webseiten',
    title: 'Hosting & Betrieb: Warum Geschwindigkeit und Sicherheit entscheidend sind',
    excerpt:
      'Gutes Hosting ist die Grundlage für schnelle, sichere und stabile Webseiten. So beeinflusst Hosting Performance, SEO und den ersten Eindruck bei Kunden.',
    category: 'Hosting & Betrieb',
    readingTime: '9 Min.',
    datePublished: '2026-01-25',
    heroImage: '/images/ratgeber/hosting-betrieb',
    heroImageAlt: 'Server-Rack im Rechenzentrum',
    cardImagePosition: '50% 45%',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead',
    },
    tags: ['Hosting', 'Performance', 'Sicherheit', 'Uptime', 'SEO', 'Agentur'],
    sections: [
      {
        heading: 'Performance als Wettbewerbsvorteil',
        paragraphs: [
          'Hosting beeinflusst die Ladezeit stärker als viele Unternehmen denken. Eine schnelle Seite sorgt für bessere Rankings, höhere Conversion und eine bessere Nutzererfahrung. Besonders in der Region Harz, wo viele Nutzer mobil suchen, zählt jede Sekunde.',
          'Ein leistungsstarkes Hosting sorgt dafür, dass Seiten auch bei höherem Traffic stabil bleiben. Wer professionell wirken will, muss auch technisch überzeugen.',
        ],
      },
      {
        heading: 'Uptime und Verfügbarkeit',
        paragraphs: [
          'Ausfälle kosten Vertrauen und Umsatz. Ein professionelles Hosting bietet Monitoring, Backup-Strategien und stabile Infrastruktur. Das ist besonders wichtig für Buchungssysteme, Kontaktformulare oder Onlineshops, bei denen jede Minute zählt.',
          'Hohe Verfügbarkeit bedeutet nicht nur Technik, sondern auch einen klaren Support-Prozess. Wenn Probleme auftreten, muss schnell reagiert werden, damit Kunden nicht verloren gehen.',
        ],
      },
      {
        heading: 'Sicherheit und DSGVO',
        paragraphs: [
          'Sicherheit ist ein Pflichtfaktor. SSL-Zertifikate, regelmäßige Updates und Schutzmechanismen gegen Angriffe sind essenziell, damit Kunden Vertrauen haben. Besonders im deutschen Markt spielt Datenschutz eine zentrale Rolle.',
          'Ein Hosting in der EU oder in Deutschland erleichtert DSGVO-Konformität. Unternehmen im Harz und in Sachsen-Anhalt profitieren von klaren Datenschutzstandards und rechtlicher Sicherheit.',
        ],
      },
      {
        heading: 'Skalierung ohne Engpässe',
        paragraphs: [
          'Wenn eine Website wächst, steigen Anforderungen an Serverleistung und Infrastruktur. Gutes Hosting skaliert mit und verhindert, dass die Website bei steigendem Traffic langsam oder instabil wird.',
          'Eine flexible Infrastruktur sorgt dafür, dass Marketingkampagnen oder saisonale Peaks nicht zu technischen Problemen führen. Das ist besonders relevant für Hotels, Praxen oder Dienstleister mit wechselnder Nachfrage.',
        ],
      },
      {
        heading: 'Technischer Betrieb als Service',
        paragraphs: [
          'Viele Unternehmen möchten sich nicht um Updates, Monitoring oder Backups kümmern. Ein professioneller Hosting- und Betriebsservice übernimmt diese Aufgaben und sorgt für Sicherheit und Stabilität.',
          'So bleibt mehr Zeit für das Kerngeschäft. Gleichzeitig ist die Website immer auf dem aktuellen Stand, was sich positiv auf SEO und Nutzererlebnis auswirkt.',
        ],
      },
    ],
    relatedServices: ['hosting-domains', 'webseitenerstellung'],
  },
  {
    slug: 'digitalisierung-beratung-prozesse',
    title: 'Digitalisierung & Beratung: Prozesse optimieren und Effizienz steigern',
    excerpt:
      'Digitalisierung bedeutet mehr als neue Tools. Sie schafft effizientere Abläufe, bessere Kundenerlebnisse und einen Wettbewerbsvorteil für Unternehmen im Harz.',
    category: 'Digitalisierung',
    readingTime: '9 Min.',
    datePublished: '2026-01-12',
    heroImage: '/images/ratgeber/digitalisierung-beratung',
    heroImageAlt: 'Whiteboard mit Haftnotizen zur Prozessplanung',
    cardImagePosition: '35% 18%',
    author: {
      name: 'Enrico Kerwel',
      title: 'Creative Director',
    },
    tags: ['Digitalisierung', 'Beratung', 'Automatisierung', 'Prozesse', 'Unternehmen'],
    sections: [
      {
        heading: 'Digitalisierung beginnt mit Klarheit',
        paragraphs: [
          'Digitalisierung ist kein Selbstzweck. Der erste Schritt ist die Analyse: Welche Prozesse kosten Zeit, wo entstehen Fehler und welche Aufgaben lassen sich automatisieren? Unternehmen, die hier systematisch vorgehen, gewinnen schnell Effizienz.',
          'Für viele Betriebe im Harz bedeutet Digitalisierung vor allem Entlastung. Weniger Papier, klare Workflows und schnellere Kommunikation sorgen für mehr Zeit im Tagesgeschäft.',
        ],
      },
      {
        heading: 'Die richtigen Tools wählen',
        paragraphs: [
          'Nicht jedes Tool passt zu jedem Unternehmen. Die Auswahl hängt von Zielen, Teamgröße und bestehender Infrastruktur ab. Gute Beratung stellt sicher, dass neue Systeme wirklich genutzt werden und Prozesse vereinfachen.',
          'Ein durchdachter Tool-Stack verhindert Insellösungen. Wenn CRM, Buchungssystem und Website sauber miteinander verbunden sind, entstehen durchgängige Abläufe.',
        ],
      },
      {
        heading: 'Automatisierung als Effizienzhebel',
        paragraphs: [
          'Automatisierung reduziert repetitive Aufgaben. Beispiele sind automatische Terminbestätigungen, Rechnungsprozesse oder interne Benachrichtigungen. Das spart Zeit und reduziert Fehler.',
          'Für Dienstleister, Hotels oder Praxen bedeutet das eine messbare Entlastung. Gleichzeitig verbessert sich der Service, weil Kunden schneller Antworten erhalten.',
        ],
      },
      {
        heading: 'Change-Management und Team-Einbindung',
        paragraphs: [
          'Technik allein reicht nicht. Digitalisierung muss im Team verankert sein, damit sie wirklich wirkt. Schulungen, klare Kommunikation und ein schrittweiser Rollout erhöhen die Akzeptanz.',
          'Unternehmen, die ihr Team früh einbinden, erzielen bessere Ergebnisse. So entsteht eine nachhaltige Veränderung statt kurzfristiger Tool-Einsatz.',
        ],
      },
      {
        heading: 'Digitalisierung als Wettbewerbsvorteil',
        paragraphs: [
          'Effiziente Prozesse bedeuten schnellere Reaktionen, bessere Kundenerlebnisse und geringere Kosten. Das verschafft Unternehmen einen klaren Vorteil gegenüber weniger digitalisierten Wettbewerbern.',
          'Wer im Harz und in Sachsen-Anhalt früh auf Digitalisierung setzt, schafft sich eine stabile Basis für Wachstum und langfristige Sichtbarkeit.',
        ],
      },
    ],
    relatedServices: ['digitalisierung-beratung', 'webseitenerstellung'],
  },
  {
    slug: 'vorteile-buchungssysteme-online-terminbuchung',
    title: 'Die wichtigsten Vorteile von Buchungssystemen',
    excerpt:
      'Buchungssysteme bringen mehr Anfragen, weniger No-Shows und bessere Auslastung. Hier sind die wichtigsten Vorteile für Dienstleister, Hotels und Praxen.',
    category: 'Buchungssysteme',
    readingTime: '8 Min.',
    datePublished: '2026-02-02',
    heroImage: '/images/ratgeber/buchungssysteme',
    heroImageAlt: 'Kalender und Stift für Terminplanung',
    cardImagePosition: '65% 18%',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead',
    },
    tags: ['Buchungssysteme', 'Online-Terminbuchung', 'Hotels', 'Praxen', 'Conversion', 'Automatisierung'],
    sections: [
      {
        heading: 'Rund-um-die-Uhr-Verfügbarkeit',
        paragraphs: [
          'Ein Buchungssystem macht Angebote jederzeit buchbar. Kunden können auch abends, am Wochenende oder unterwegs Termine reservieren. Dadurch entstehen neue Anfragen, die sonst verloren gehen würden. Für Dienstleister im Harz ist das ein klarer Wettbewerbsvorteil.',
          'Diese Verfügbarkeit wirkt sich direkt auf die Conversion aus. Je schneller ein Interessent buchen kann, desto höher die Abschlussrate. Online-Terminbuchung ist damit nicht nur komfortabel, sondern ein messbarer Umsatzhebel.',
        ],
      },
      {
        heading: 'Weniger No-Shows und stabilere Auslastung',
        paragraphs: [
          'Automatische Erinnerungen reduzieren No-Shows deutlich. Kunden erhalten rechtzeitig E-Mail- oder SMS-Benachrichtigungen und halten ihre Termine zuverlässiger ein. Das verbessert die Planungssicherheit und senkt Verluste.',
          'Auch für Hotels oder Praxen ist das entscheidend. Eine stabile Auslastung sorgt für konstante Einnahmen und weniger Stress im Tagesgeschäft.',
        ],
      },
      {
        heading: 'Self-Service und Kundenzufriedenheit',
        paragraphs: [
          'Kunden schätzen es, Termine selbst zu verwalten. Umbuchungen oder Stornierungen sind ohne Telefonate möglich. Das erhöht die Zufriedenheit und stärkt die Bindung an das Unternehmen.',
          'Ein komfortabler Self-Service wirkt professionell und modern. Gerade in der Dienstleistungsbranche ist das ein wichtiger Faktor für wiederkehrende Kunden.',
        ],
      },
      {
        heading: 'Effizientere Abläufe im Team',
        paragraphs: [
          'Weniger Telefonate und weniger manuelle Terminverwaltung bedeuten mehr Zeit für die eigentliche Arbeit. Automatisierte Prozesse reduzieren Fehler und steigern die Produktivität.',
          'Besonders kleine Teams profitieren von dieser Entlastung. Statt Terminkoordination bleibt mehr Raum für Qualität und Kundenservice.',
        ],
      },
      {
        heading: 'Stärkere SEO- und Local-SEO-Signale',
        paragraphs: [
          'Buchungssysteme erhöhen die Interaktion auf der Website. Nutzer bleiben länger, klicken mehr und führen konkrete Aktionen aus. Diese Signale wirken sich positiv auf Google-Rankings aus.',
          'Für lokale Anbieter ist das besonders wertvoll. Wer bei „Online-Terminbuchung Wernigerode“ sichtbar ist und eine einfache Buchung anbietet, gewinnt mehr Kunden und bessere Bewertungen.',
        ],
      },
      {
        heading: 'Daten für bessere Entscheidungen',
        paragraphs: [
          'Ein gutes System liefert Zahlen zu Auslastung, Stoßzeiten und Stornierungen. Diese Daten helfen, Angebote besser zu planen und Marketing gezielt auszurichten.',
          'Unternehmen, die datenbasiert arbeiten, reagieren schneller auf Veränderungen und können ihre Prozesse kontinuierlich verbessern.',
        ],
      },
      {
        heading: 'Zahlungsabwicklung und Cashflow',
        paragraphs: [
          'Viele Buchungssysteme ermöglichen Vorauszahlungen oder Teilzahlungen. Das verbessert den Cashflow und reduziert Zahlungsausfälle. Besonders in der Hotellerie oder bei Beratungsleistungen ist das ein großer Vorteil.',
          'Klare Zahlungsprozesse wirken professionell und schaffen zusätzliches Vertrauen bei Kunden.',
        ],
      },
      {
        heading: 'Skalierung und Wachstum ohne Chaos',
        paragraphs: [
          'Wenn ein Unternehmen wächst, steigt die Komplexität. Ein Buchungssystem skaliert mit und ermöglicht mehrere Mitarbeiter, Standorte oder Leistungsarten. Damit bleibt Wachstum steuerbar und Prozesse bleiben stabil.',
          'Wer früh digitalisiert, kann neue Zielgruppen schneller erschließen und bleibt langfristig wettbewerbsfähig.',
        ],
      },
      {
        heading: 'Professioneller Außenauftritt',
        paragraphs: [
          'Ein modernes Buchungssystem wirkt innovativ und zeigt, dass Prozesse klar geregelt sind. Kunden sehen sofort, dass sie es mit einem professionellen Anbieter zu tun haben.',
          'Gerade in wettbewerbsintensiven Branchen wie Hotels, Praxen oder Dienstleistungen ist dieser erste Eindruck entscheidend für die Buchungsentscheidung.',
        ],
      },
    ],
    relatedServices: ['buchungssysteme', 'webseitenerstellung', 'seo-optimierung'],
  },
  {
    slug: 'barrierefreiheit-bfsg-webseiten',
    title: 'Digitale Barrierefreiheit (BFSG): Was Unternehmen jetzt beachten müssen',
    excerpt:
      'Ab Juni 2025 gilt das Barriere­freiheits­stärkungs­gesetz (BFSG). Welche Webseiten betroffen sind, was zu tun ist und wie Sie rechtzeitig handeln.',
    category: 'Barrierefreiheit',
    readingTime: '11 Min.',
    datePublished: '2026-02-05',
    heroImage: '/images/ratgeber/barrierefreiheit-bfsg',
    heroImageAlt: 'Laptop mit Barrierefreiheits-Icons und Checkliste',
    cardImagePosition: '50% 40%',
    author: {
      name: 'Jens Goldhammer',
      title: 'Technical Lead',
    },
    tags: ['Barrierefreiheit', 'BFSG', 'WCAG', 'Accessibility', 'Gesetz', 'Webdesign'],
    sections: [
      {
        heading: 'Was ist das BFSG?',
        paragraphs: [
          'Das Barriere­freiheits­stärkungs­gesetz (BFSG) setzt die EU-Richtlinie zur Barrierefreiheit in deutsches Recht um. Ab dem 28. Juni 2025 müssen digitale Produkte und Dienstleistungen barrierefrei gestaltet sein. Das betrifft auch Webseiten und Onlineshops, die Verbrauchern angeboten werden.',
          'Ziel des Gesetzes ist es, Menschen mit Behinderungen gleichberechtigten Zugang zu digitalen Angeboten zu ermöglichen. Das umfasst Sehbehinderungen, Hörbehinderungen, motorische Einschränkungen und kognitive Beeinträchtigungen. Barrierefreiheit bedeutet, dass alle Nutzer eine Webseite wahrnehmen, verstehen, navigieren und bedienen können.',
        ],
      },
      {
        heading: 'Wer ist vom BFSG betroffen?',
        paragraphs: [
          'Das BFSG gilt für Unternehmen, die digitale Produkte oder Dienstleistungen an Verbraucher (B2C) anbieten. Dazu zählen Webseiten mit Online­shops, Buchungs­systemen, Kontakt­formularen oder anderen interaktiven Funktionen. Auch Banking-Apps, E-Books, Ticketautomaten und bestimmte Hardware fallen unter das Gesetz.',
          'Kleine Unternehmen mit weniger als 10 Mitarbeitern und einem Jahres­umsatz unter 2 Millionen Euro können unter bestimmten Bedingungen ausgenommen sein. Allerdings gilt die Ausnahme nicht für alle Bereiche, und auch kleine Anbieter sollten prüfen, ob ihre Angebote betroffen sind. B2B-Angebote, die ausschließlich an Geschäftskunden gerichtet sind, fallen nicht unter das BFSG.',
        ],
      },
      {
        heading: 'Welche Anforderungen stellt das BFSG?',
        paragraphs: [
          'Die technischen Anforderungen orientieren sich an den Web Content Accessibility Guidelines (WCAG) 2.1 auf Stufe AA. Diese internationalen Richtlinien definieren vier Grundprinzipien: Webseiten müssen wahrnehmbar, bedienbar, verständlich und robust sein. Konkret bedeutet das unter anderem ausreichende Farbkontraste, Tastatur­bedienbarkeit, Alt-Texte für Bilder und eine klare Seitenstruktur.',
          'Formulare müssen verständliche Fehlermeldungen liefern, Videos sollten Untertitel haben, und PDFs müssen barrierefrei erstellt werden. Auch mobile Webseiten und Apps müssen die Anforderungen erfüllen. Eine saubere semantische HTML-Struktur bildet die Grundlage für barrierefreie Inhalte.',
        ],
      },
      {
        heading: 'Praktische Maßnahmen für Webseiten',
        paragraphs: [
          'Der erste Schritt ist eine Bestands­aufnahme. Welche Barrieren bestehen aktuell? Tools wie WAVE, axe oder Lighthouse können automatische Prüfungen durchführen. Allerdings erkennen diese nur etwa 30 bis 40 Prozent aller Probleme. Eine manuelle Prüfung durch Experten ist für eine vollständige Bewertung notwendig.',
          'Typische Maßnahmen umfassen: Alle Bilder mit aussage­kräftigen Alt-Texten versehen, Formulare mit Labels verknüpfen, Fokus­reihenfolge für Tastatur­navigation prüfen, Farbkontraste anpassen und Überschriften­hierarchie korrigieren. Interaktive Elemente wie Modals, Dropdowns und Akkordeons müssen mit ARIA-Attributen für Screen­reader zugänglich gemacht werden.',
        ],
      },
      {
        heading: 'Zeitplan und Übergangsfristen',
        paragraphs: [
          'Das BFSG gilt ab dem 28. Juni 2025 für neue Produkte und Dienstleistungen. Für bestehende Angebote gelten Übergangs­fristen: Webseiten und digitale Dienste, die vor diesem Datum bestanden, müssen bis spätestens 2030 barrierefrei sein. Allerdings sollten Unternehmen nicht bis zum letzten Moment warten.',
          'Eine schrittweise Umsetzung ist sinnvoll. Beginnen Sie mit den wichtigsten Nutzer­pfaden wie Kontakt­formularen, Buchungs­strecken und der Navigation. Priorisieren Sie kritische Barrieren, die Nutzer komplett ausschließen. So verteilen Sie den Aufwand und vermeiden Stress vor der Deadline.',
        ],
      },
      {
        heading: 'Konsequenzen bei Nicht­einhaltung',
        paragraphs: [
          'Bei Verstößen gegen das BFSG drohen Bußgelder von bis zu 100.000 Euro. Die Markt­überwachungs­behörden der Bundes­länder sind für die Kontrolle zuständig. Zusätzlich können Verbände Unterlassungs­klagen einreichen, und Wettbewerber könnten Abmahnungen aussprechen.',
          'Neben rechtlichen Konsequenzen besteht auch ein Reputationsrisiko. Unternehmen, die Menschen mit Behinderungen ausschließen, können öffentlich kritisiert werden. Umgekehrt bietet Barrierefreiheit die Chance, sich als inklusives Unternehmen zu positionieren.',
        ],
      },
      {
        heading: 'Barrierefreiheit als Chance',
        paragraphs: [
          'Barrierefreie Webseiten sind nicht nur gesetzlich gefordert, sondern bieten auch geschäftliche Vorteile. Sie erreichen mehr Menschen, verbessern die User Experience für alle Nutzer und stärken die SEO. Google bewertet technisch saubere, gut strukturierte Seiten positiv.',
          'Viele Maßnahmen für Barrierefreiheit überschneiden sich mit Best Practices für Webdesign: klare Struktur, schnelle Ladezeiten, verständliche Texte und intuitive Navigation. Wer jetzt investiert, profitiert langfristig von einer besseren Webseite und erreicht eine breitere Zielgruppe.',
        ],
      },
      {
        heading: 'Erste Schritte für Ihr Unternehmen',
        paragraphs: [
          'Prüfen Sie zunächst, ob Ihr Angebot unter das BFSG fällt. Führen Sie dann eine Barriere­freiheits­prüfung Ihrer Webseite durch. Identifizieren Sie die größten Probleme und erstellen Sie einen Umsetzungsplan mit klaren Prioritäten und Zeitrahmen.',
          'Bei einer Agentur wie Enno & Jens erhalten Sie Unterstützung bei der Analyse, Konzeption und technischen Umsetzung. Wir helfen Ihnen, Ihre Webseite rechtzeitig barrierefrei zu gestalten und die gesetzlichen Anforderungen zu erfüllen.',
        ],
      },
    ],
    relatedServices: ['webseitenerstellung', 'beratung'],
  },
];

export const getInsightBySlug = (slug: string) => insights.find((insight) => insight.slug === slug);

export const getLatestInsights = (count = 3) =>
  [...insights]
    .sort((a, b) => (a.datePublished < b.datePublished ? 1 : -1))
    .slice(0, count);
